<script setup>
import { MediaImage, vMediaPoster, vMediaBackground } from '@drama/media-delivery/vue'
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import LandingLiveRooms from '../components/LandingLiveRooms.vue'
import SiteTopNav from '../components/SiteTopNav.vue'
import { parseVisualGameScenarioListItem, parseVisualGameScenarioVideoList } from '@drama/contracts'
import ShowcaseVideoList from '../components/ShowcaseVideoList.vue'
import VgIcon from '../components/trpg/icons/VgIcon.vue'
import { createVisualAccount } from '../composables'
import { sortScenarioVideos, videoSeriesHref } from '../lib/video-center.js'
import { storyCoverUrl, storyLandscapeCoverUrl } from '../lib/scenario-cover.js'

const api = inject('api')
if (!api) throw new Error('LandingPage: api provider is required')
const analytics = inject('analytics')
if (!analytics) throw new Error('LandingPage: analytics provider is required')

const { t, locale } = useI18n()
const account = createVisualAccount(api, { analytics, getLocale: () => locale.value })
const topNav = ref(null)
const scenarios = ref([])
const videosByScenarioId = ref({})
const previewVideosByScenarioId = ref({})
const carouselItems = ref([])
const query = ref('')
const sort = ref('popular')
const dramaFilter = ref('all')
const publishedWithin = ref('all')
const loading = ref(true)
const error = ref('')
const loadedShowcaseCount = ref(12)
const showcaseLoadMoreSentinel = ref(null)
const activeTopSlide = ref(0)
const topSlideDragX = ref(0)
const topSlideDragging = ref(false)
const topSlideTrackPosition = ref(1)
const topSlideTrackTransitionEnabled = ref(true)
const officialVideoOpen = ref(false)
// 首屏预览视频带 autoplay，会绕过 preload=metadata 直接拉完整文件（实测 1379 KB，
// 占整页一半字节）。等 load 之后再挂载，首屏只显示封面，不和关键资源抢带宽。
const heroPreviewEnabled = ref(false)
const activeStoryPreviewId = ref('')
const selectedStoryChannel = ref('featured')
const storyQuery = ref('')
const storyChannelMoreOpen = ref(false)
const storyChannelMore = ref(null)
const isMobileLanding = globalThis.matchMedia('(max-width: 680px)').matches
const SHOWCASE_PAGE_SIZE = 12
// PC 端标签只露最常用的 10 个，其余收进 More，筛选行不再挤成两行；手机端标签是横滑的，全部露出
const VISIBLE_GENRE_TAG_COUNT = 10
const TOP_SLIDE_AUTOPLAY_MS = 6000
let showcaseLoadObserver = null
let topSlideAutoplayTimer = null
let topSlideLoopResetTimer = null
let topSlideTouchStart = null
let topSlideSuppressClickUntil = 0
const accountRuns = ref([])
const accountRunsLoading = ref(false)
const accountRunsLoaded = ref(false)
const LANDING_CACHE_KEY = 'visualGame.landingCache.v11'
const LANDING_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000
const footerLinks = {
  facebook: 'https://www.facebook.com/profile.php?id=61593699586167',
  x: 'https://x.com/vokoworld',
  youtube: 'https://www.youtube.com/@voko_world',
  instagram: 'https://www.instagram.com/vokoworld/',
  tiktok: 'https://www.tiktok.com/@vokodramagame',
  discord: 'https://discord.gg/WeESGyuUSX',
  privacy: '/legal/privacy',
  terms: '/legal/terms',
  contact: '/contact',
}

const showcaseVideoItems = computed(() => scenarios.value.flatMap(scenario => {
  return videosByScenarioId.value[scenario.id].map(video => showcaseCardFromData(scenario, video))
}))
const storyCards = computed(() => groupStoryCards(scenarios.value, previewVideosByScenarioId.value))
// 常用 = 挂这个标签的故事多
const storyTags = computed(() => {
  const storyCountByTag = new Map()
  for (const story of storyCards.value) {
    for (const tag of story.tags) storyCountByTag.set(tag, (storyCountByTag.get(tag) || 0) + 1)
  }
  return [...storyCountByTag.keys()].sort((left, right) => {
    return storyCountByTag.get(right) - storyCountByTag.get(left) || left.localeCompare(right, locale.value)
  })
})
const storyChannels = computed(() => [
  { id: 'featured', label: t('visualGame.landingCinema.featuredDramas'), overflow: false },
  { id: 'latest', label: t('visualGame.landing.latest'), overflow: false },
  { id: 'popular', label: t('visualGame.landing.popular'), overflow: false },
  ...storyTags.value.map((tag, index) => ({ id: `genre:${tag}`, label: tag, overflow: index >= VISIBLE_GENRE_TAG_COUNT })),
])
const overflowStoryChannels = computed(() => storyChannels.value.filter(channel => channel.overflow))
const selectedOverflowChannel = computed(() => overflowStoryChannels.value.find(channel => channel.id === selectedStoryChannel.value) || null)
const filteredStoryCards = computed(() => {
  const needle = storyQuery.value.trim().toLocaleLowerCase(locale.value)
  const selectedGenre = selectedStoryChannel.value.startsWith('genre:')
    ? selectedStoryChannel.value.slice('genre:'.length)
    : null
  return storyCards.value.filter(story => {
    const matchesChannel = selectedGenre === null || story.tags.includes(selectedGenre)
    const searchableText = `${story.title} ${story.summary} ${story.tags.join(' ')}`.toLocaleLowerCase(locale.value)
    return matchesChannel && (needle === '' || searchableText.includes(needle))
  })
})
const landingStoryCards = computed(() => {
  return [...filteredStoryCards.value].sort((left, right) => {
    const pinned = comparePinnedStories(left, right)
    if (pinned !== 0) return pinned
    return right.playCount - left.playCount || left.title.localeCompare(right.title, locale.value)
  }).map(story => ({
    ...story,
    storyLineHref: storyLineHrefForStory(story, accountRuns.value),
  }))
})
const landingStorySections = computed(() => {
  const stories = landingStoryCards.value
  if (stories.length === 0) return []
  const popular = [...stories].sort((left, right) => right.playCount - left.playCount || left.title.localeCompare(right.title, locale.value))
  const latest = [...stories].sort((left, right) => right.updatedAt - left.updatedAt || right.playCount - left.playCount)
  switch (selectedStoryChannel.value) {
    case 'featured':
      return [
        { id: 'featured', title: t('visualGame.landingCinema.featuredDramas'), items: stories, mobileOnly: false },
        { id: 'popular', title: t('visualGame.landing.popular'), items: popular, mobileOnly: true },
        { id: 'latest', title: t('visualGame.landing.latest'), items: latest, mobileOnly: true },
        ...storyTags.value
          .map(tag => ({
            id: `genre:${tag}`,
            title: tag,
            items: popular.filter(story => story.tags.includes(tag)),
            mobileOnly: true,
          }))
          .filter(section => section.items.length >= 3)
          .sort((left, right) => right.items.length - left.items.length || left.title.localeCompare(right.title, locale.value))
          .slice(0, 2),
      ]
    case 'popular':
      return [{ id: 'popular', title: t('visualGame.landing.popular'), items: popular, mobileOnly: false }]
    case 'latest':
      return [{ id: 'latest', title: t('visualGame.landing.latest'), items: latest, mobileOnly: false }]
    default: {
      if (!selectedStoryChannel.value.startsWith('genre:')) {
        throw new Error(`Unhandled landing story channel: ${selectedStoryChannel.value}`)
      }
      const channel = storyChannels.value.find(item => item.id === selectedStoryChannel.value)
      if (!channel) throw new Error(`Landing story genre channel is missing: ${selectedStoryChannel.value}`)
      return [{ id: selectedStoryChannel.value, title: channel.label, items: popular, mobileOnly: false }]
    }
  }
})
const backfilledPreviewScenarioIds = new Set()
watch(landingStorySections, backfillGamePreviewVideos, { immediate: true })

async function backfillGamePreviewVideos() {
  const missingScenarioIds = [...new Set(
    landingStorySections.value
      .flatMap(section => section.items.slice(0, 10))
      .filter(story => !story.previewVideoUrl)
      .flatMap(story => story.scenarioIds),
  )].filter(id => !backfilledPreviewScenarioIds.has(id))
  if (missingScenarioIds.length === 0) return
  for (const id of missingScenarioIds) backfilledPreviewScenarioIds.add(id)
  const results = await Promise.all(missingScenarioIds.map(async id => {
    const result = await api.visualGame.listScenarioVideos(id)
    return [id, result.videos]
  }))
  const withNewVideos = results.filter(([, videos]) => videos.length > 0)
  if (withNewVideos.length === 0) return
  previewVideosByScenarioId.value = {
    ...previewVideosByScenarioId.value,
    ...Object.fromEntries(withNewVideos.map(([id, videos]) => [id, sortScenarioVideos(videos, 'latest')])),
  }
}

const showcaseVideos = computed(() => groupSeasonCards(showcaseVideoItems.value))
const publishedShowcaseVideos = computed(() => showcaseVideos.value.filter(matchesPublishedWithin))
const filteredShowcaseVideos = computed(() => publishedShowcaseVideos.value.filter(matchesDramaFilter))
const showcaseGroups = computed(() => {
  const normalizedQuery = query.value.trim().toLocaleLowerCase(locale.value)
  if (normalizedQuery) {
    const matchedPlayers = new Map()
    for (const item of filteredShowcaseVideos.value) {
      if (item.video.player_id === null) continue
      if (!item.video.player_name.toLocaleLowerCase(locale.value).includes(normalizedQuery)) continue
      matchedPlayers.set(item.video.player_id, item.video.player_name)
    }
    if (matchedPlayers.size > 0) {
      return [...matchedPlayers].map(([playerId, playerName]) => ({
        id: `player:${playerId}`,
        playerId,
        playerName,
        items: sortShowcaseVideos(filteredShowcaseVideos.value.filter(item => item.video.player_id === playerId)),
      }))
    }
  }
  const items = normalizedQuery
    ? filteredShowcaseVideos.value.filter(item => showcaseContentSearchText(item).includes(normalizedQuery))
    : filteredShowcaseVideos.value
  return [{ id: 'videos', playerId: '', playerName: '', items: sortShowcaseVideos(items) }]
})
const visibleShowcaseVideos = computed(() => showcaseGroups.value.flatMap(group => group.items))
const landingDramaGroups = computed(() => {
  let remaining = 12
  return showcaseGroups.value.flatMap(group => {
    if (remaining <= 0) return []
    const items = group.items.slice(0, remaining)
    remaining -= items.length
    return items.length === 0 ? [] : [{ ...group, items }]
  })
})
const videosMoreHref = computed(() => {
  const params = new URLSearchParams()
  const normalizedQuery = query.value.trim()
  if (normalizedQuery) params.set('query', normalizedQuery)
  if (sort.value !== 'popular') params.set('sort', sort.value)
  if (dramaFilter.value !== 'all') params.set('drama', dramaFilter.value)
  if (publishedWithin.value !== 'all') params.set('published', publishedWithin.value)
  const queryString = params.toString()
  return queryString ? `/dramas?${queryString}` : '/dramas'
})
const hasMoreShowcaseVideos = computed(() => loadedShowcaseCount.value < visibleShowcaseVideos.value.length)
// 后台没配轮播时的兜底：只放热门故事。宣传视频新访客看不懂、也没有开始游戏的入口，不再放
const defaultTopSlides = computed(() => [isMistboundStory, isPaintedSkinStory].flatMap((matchesStory, index) => {
  const story = storyCards.value.find(matchesStory)
  return story === undefined ? [] : [storySlideFromStory(story, index)]
}))
const topSlides = computed(() => {
  if (carouselItems.value.length === 0) return defaultTopSlides.value
  return carouselItems.value.flatMap((item, index) => {
    if (item.item_type === 'youtube') return [{
      id: item.id,
      kind: 'platform-video',
      eyebrow: item.eyebrow || t('visualGame.landing.officialVideoEyebrow'),
      title: item.title || t('visualGame.landing.officialVideoTitle'),
      summary: item.summary || t('visualGame.landing.officialVideoBody'),
      coverUrl: `https://i.ytimg.com/vi/${encodeURIComponent(item.reference_id)}/maxresdefault.jpg`,
      desktopCoverUrl: `https://i.ytimg.com/vi/${encodeURIComponent(item.reference_id)}/maxresdefault.jpg`,
      thumbCoverUrl: `https://i.ytimg.com/vi/${encodeURIComponent(item.reference_id)}/mqdefault.jpg`,
      playerEmbedUrl: `https://www.youtube.com/embed/${encodeURIComponent(item.reference_id)}?autoplay=1&playsinline=1&rel=0&controls=1&origin=${encodeURIComponent(globalThis.location.origin)}`,
    }]
    const story = storyCards.value.find(candidate => candidate.id === item.reference_id)
    return story ? [storySlideFromStory(story, index, item)] : []
  })
})
const activeTopSlideData = computed(() => topSlides.value[activeTopSlide.value] || topSlides.value[0])
const topSlideTrackSlides = computed(() => {
  const slides = topSlides.value
  if (slides.length === 0) return []
  return [slides[slides.length - 1], ...slides, slides[0]]
})
const topSlideTrackStyle = computed(() => ({
  transform: `translate3d(calc(${-topSlideTrackPosition.value * 100}% + ${topSlideDragX.value}px), 0, 0)`,
}))

function storySlideFromStory(story, index, overrides = {}) {
  const storyScenarios = scenarios.value.filter(scenario => story.scenarioIds.includes(scenario.id))
  const storyRuns = accountRuns.value.filter(run => story.scenarioIds.includes(run.scenario_id))
  const continueTarget = resolveContinueGameTarget(storyRuns, storyScenarios)
  return {
    id: story.id,
    kind: 'story',
    eyebrow: overrides.eyebrow || t('visualGame.landingCarousel.dramaEyebrow', { count: index + 1 }),
    title: overrides.title || story.title,
    summary: overrides.summary || story.summary || t('visualGame.landing.scenarioFallback'),
    coverUrl: story.coverUrl,
    desktopCoverUrl: story.heroCoverUrl,
    thumbCoverUrl: story.thumbCoverUrl,
    previewVideoUrl: story.previewVideoUrl,
    detailHref: story.detailHref,
    playState: story.playState,
    playHref: story.playHref,
    continueHref: continueTarget === null ? '' : continueTarget.href,
    watchHref: story.watchHref || '/dramas',
    storyLineHref: storyLineHrefForStory(story, accountRuns.value),
  }
}

function sortShowcaseVideos(items) {
  switch (sort.value) {
    case 'latest':
      return [...items].sort((a, b) => showcaseVideoTimestamp(b.video) - showcaseVideoTimestamp(a.video))
    case 'popular':
      return [...items].sort((a, b) => b.video.play_count - a.video.play_count || showcaseVideoTimestamp(b.video) - showcaseVideoTimestamp(a.video))
    default:
      throw new Error(`Unhandled landing sort: ${sort.value}`)
  }
}

function showTopSlide(index) {
  officialVideoOpen.value = false
  if (topSlideLoopResetTimer !== null) globalThis.clearTimeout(topSlideLoopResetTimer)
  topSlideDragX.value = 0
  topSlideDragging.value = false
  topSlideTrackTransitionEnabled.value = true
  const slideCount = topSlides.value.length
  const normalizedIndex = (index + slideCount) % slideCount
  const isForwardLoop = activeTopSlide.value === slideCount - 1 && index >= slideCount
  const isBackwardLoop = activeTopSlide.value === 0 && index < 0
  topSlideTrackPosition.value = isForwardLoop ? slideCount + 1 : isBackwardLoop ? 0 : normalizedIndex + 1
  activeTopSlide.value = normalizedIndex
  if (isForwardLoop || isBackwardLoop) {
    topSlideLoopResetTimer = globalThis.setTimeout(async () => {
      topSlideTrackTransitionEnabled.value = false
      await nextTick()
      topSlideTrackPosition.value = normalizedIndex + 1
      await nextTick()
      globalThis.requestAnimationFrame(() => {
        globalThis.requestAnimationFrame(() => {
          topSlideTrackTransitionEnabled.value = true
        })
      })
      topSlideLoopResetTimer = null
    }, 470)
  }
  scheduleTopSlideAutoplay()
}

function startTopSlideTouch(event) {
  const touch = event.touches[0]
  topSlideTouchStart = { x: touch.clientX, y: touch.clientY }
  topSlideDragging.value = false
  topSlideDragX.value = 0
  stopTopSlideAutoplay()
}

function moveTopSlideTouch(event) {
  if (topSlideTouchStart === null) return
  const touch = event.touches[0]
  const deltaX = touch.clientX - topSlideTouchStart.x
  const deltaY = touch.clientY - topSlideTouchStart.y
  if (!topSlideDragging.value && Math.abs(deltaX) <= Math.abs(deltaY)) return
  topSlideDragging.value = true
  topSlideDragX.value = deltaX
}

function endTopSlideTouch(event) {
  if (topSlideTouchStart === null) return
  const touch = event.changedTouches[0]
  const deltaX = touch.clientX - topSlideTouchStart.x
  const deltaY = touch.clientY - topSlideTouchStart.y
  topSlideTouchStart = null
  if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY)) {
    topSlideDragX.value = 0
    topSlideDragging.value = false
    scheduleTopSlideAutoplay()
    return
  }
  topSlideSuppressClickUntil = Date.now() + 400
  showTopSlide(activeTopSlide.value + (deltaX < 0 ? 1 : -1))
}

function cancelTopSlideTouch() {
  topSlideTouchStart = null
  topSlideDragX.value = 0
  topSlideDragging.value = false
  scheduleTopSlideAutoplay()
}

function openTopSlideDetail(event) {
  if (Date.now() >= topSlideSuppressClickUntil) return
  event.preventDefault()
}

function openOfficialVideo() {
  officialVideoOpen.value = true
  stopTopSlideAutoplay()
}

function openOfficialVideoFromSlide(event) {
  if (Date.now() < topSlideSuppressClickUntil) {
    event.preventDefault()
    return
  }
  openOfficialVideo()
}

function closeOfficialVideo() {
  officialVideoOpen.value = false
  scheduleTopSlideAutoplay()
}

function stopTopSlideAutoplay() {
  if (topSlideAutoplayTimer === null) return
  globalThis.clearTimeout(topSlideAutoplayTimer)
  topSlideAutoplayTimer = null
}

function scheduleTopSlideAutoplay() {
  stopTopSlideAutoplay()
  if (document.hidden || officialVideoOpen.value || topSlides.value.length < 2) return
  if (globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  topSlideAutoplayTimer = globalThis.setTimeout(() => {
    showTopSlide(activeTopSlide.value + 1)
  }, TOP_SLIDE_AUTOPLAY_MS)
}

function handleDocumentVisibilityChange() {
  if (document.hidden) {
    stopTopSlideAutoplay()
    return
  }
  scheduleTopSlideAutoplay()
}

async function playStoryPreview(event) {
  if (globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  const video = event.currentTarget.querySelector('video')
  if (video === null) return
  try {
    await video.play()
  } catch (cause) {
    console.debug('Landing story preview could not autoplay', { cause, video_src: video.currentSrc })
  }
}

function stopStoryPreview(event) {
  const video = event.currentTarget.querySelector('video')
  if (video !== null) {
    video.pause()
    video.currentTime = 0
  }
  activeStoryPreviewId.value = ''
}

async function startStoryPreview(event, story, previewId = story.id) {
  if (!story.previewVideoUrl) return
  activeStoryPreviewId.value = previewId
  await nextTick()
  await playStoryPreview(event)
}

function storyPreviewIsActive(previewId) {
  return activeStoryPreviewId.value === previewId
}

onMounted(() => {
  document.addEventListener('pointerdown', closeStoryChannelMoreOnOutsidePress)
  document.addEventListener('keydown', closeStoryChannelMoreOnEscape)
  enableHeroPreviewAfterLoad()
  if (typeof IntersectionObserver === 'function') {
    showcaseLoadObserver = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting) || !hasMoreShowcaseVideos.value) return
      loadedShowcaseCount.value += SHOWCASE_PAGE_SIZE
    }, { rootMargin: '320px 0px' })
  }
  restoreAccountSession()
  globalThis.addEventListener('visual-game-auth-changed', restoreAccountSession)
  document.addEventListener('visibilitychange', handleDocumentVisibilityChange)
  scheduleTopSlideAutoplay()
  const cached = readLandingCache()
  if (cached) {
    applyLandingData(cached)
    loading.value = false
    loadLanding({ background: true })
    return
  }
  loadLanding()
})

function enableHeroPreviewAfterLoad() {
  if (document.readyState === 'complete') {
    heroPreviewEnabled.value = true
    return
  }
  globalThis.addEventListener('load', () => { heroPreviewEnabled.value = true }, { once: true })
}

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', closeStoryChannelMoreOnOutsidePress)
  document.removeEventListener('keydown', closeStoryChannelMoreOnEscape)
  showcaseLoadObserver?.disconnect()
  stopTopSlideAutoplay()
  if (topSlideLoopResetTimer !== null) globalThis.clearTimeout(topSlideLoopResetTimer)
  globalThis.removeEventListener('visual-game-auth-changed', restoreAccountSession)
  document.removeEventListener('visibilitychange', handleDocumentVisibilityChange)
})

watch(showcaseLoadMoreSentinel, (next, previous) => {
  if (previous) showcaseLoadObserver?.unobserve(previous)
  if (next) showcaseLoadObserver?.observe(next)
})

watch(() => topSlides.value.length, () => {
  topSlideTrackPosition.value = activeTopSlide.value + 1
  scheduleTopSlideAutoplay()
})

watch([query, sort, dramaFilter, publishedWithin], () => {
  loadedShowcaseCount.value = SHOWCASE_PAGE_SIZE
})

watch(locale, () => {
  selectedStoryChannel.value = 'featured'
  const cached = readLandingCache()
  if (cached) applyLandingData(cached)
  loadLanding({ background: Boolean(cached) })
})

async function loadLanding({ background = false } = {}) {
  if (!background) loading.value = true
  if (!background) error.value = ''
  try {
    const [nextScenarios, showcaseResult, previewResult, carouselResult] = await Promise.all([
      api.visualGame.listScenarios(),
      api.visualGame.listShowcaseVideos(),
      api.visualGame.listShowcaseVideos({ sort: 'popular' }),
      api.visualGame.listHomepageCarousel ? api.visualGame.listHomepageCarousel() : Promise.resolve({ items: [] }),
    ])
    const nextData = {
      scenarios: nextScenarios,
      videosByScenarioId: Object.fromEntries(videosByScenarioEntries(nextScenarios, showcaseResult.videos, 'showcase')),
      previewVideosByScenarioId: Object.fromEntries(videosByScenarioEntries(nextScenarios, previewResult.videos, 'preview')),
      carouselItems: carouselResult.items,
    }
    applyLandingData(nextData)
    writeLandingCache(nextData)
  } catch {
    if (!background) error.value = t('visualGame.landing.loadError')
  } finally {
    if (!background) loading.value = false
  }
}

async function restoreAccountSession() {
  await account.load()
  if (account.isSignedIn.value) await loadAccountRuns()
}

async function loadAccountRuns() {
  accountRunsLoading.value = true
  accountRunsLoaded.value = false
  try {
    const result = await api.visualGame.listRuns()
    accountRuns.value = result.runs
    accountRunsLoaded.value = true
  } finally {
    accountRunsLoading.value = false
  }
}

function resolveContinueGameTarget(runs, availableScenarios) {
  const latestRun = runs[0]
  if (!latestRun) return null
  if (latestRun.status === 'active') return resumeRunTarget(latestRun, availableScenarios)
  if (latestRun.status !== 'completed') {
    throw new Error(`Unhandled run status: ${latestRun.status}, id: ${latestRun.run_id}`)
  }
  if (latestRun.season_id === null) return null

  const latestScenario = availableScenarios.find(scenario => scenario.id === latestRun.scenario_id)
  if (!latestScenario || latestScenario.series_id === null) return null
  const seasonRuns = new Map(
    runs
      .filter(run => run.season_id === latestRun.season_id)
      .map(run => [run.scenario_id, run]),
  )
  const seriesScenarios = availableScenarios
    .filter(scenario => scenario.series_id === latestScenario.series_id)
    .filter(scenarioIsPlayable)
    .sort((left, right) => left.episode_no - right.episode_no)

  for (const scenario of seriesScenarios) {
    const run = seasonRuns.get(scenario.id)
    if (!run) {
      return {
        href: `/game?${new URLSearchParams({
          scenarioId: scenario.id,
          seasonId: latestRun.season_id,
        }).toString()}`,
        seasonNo: latestRun.season_no,
        seasonId: latestRun.season_id,
        scenarioTitle: scenario.title,
      }
    }
    switch (run.status) {
      case 'active':
        return resumeRunTarget(run, availableScenarios)
      case 'completed':
        break
      default:
        throw new Error(`Unhandled run status: ${run.status}, id: ${run.run_id}`)
    }
  }
  return null
}

function resumeRunTarget(run, availableScenarios) {
  const scenario = availableScenarios.find(item => item.id === run.scenario_id)
  return {
    href: `/game?${new URLSearchParams({
      scenarioId: run.scenario_id,
      runId: run.run_id,
    }).toString()}`,
    seasonNo: run.season_no,
    seasonId: run.season_id,
    scenarioTitle: scenario?.title || '',
  }
}

function applyLandingData(data) {
  videosByScenarioId.value = data.videosByScenarioId
  previewVideosByScenarioId.value = data.previewVideosByScenarioId
  scenarios.value = data.scenarios
  carouselItems.value = data.carouselItems || []
}

function videosByScenarioEntries(nextScenarios, sourceVideos, label) {
  const videosByScenario = new Map(nextScenarios.map(scenario => [scenario.id, []]))
  for (const video of sourceVideos) {
    const scenarioVideos = videosByScenario.get(video.scenario_id)
    if (!scenarioVideos) throw new Error(`Landing ${label} video scenario is missing: ${video.scenario_id}, video: ${video.id}`)
    scenarioVideos.push(video)
  }
  return nextScenarios.map(scenario => [
    scenario.id,
    sortScenarioVideos(videosByScenario.get(scenario.id), 'latest'),
  ])
}

function readLandingCache() {
  try {
    const raw = localStorage.getItem(landingCacheKey())
    if (!raw) return null
    const payload = JSON.parse(raw)
    if (payload.version !== 1) throw new Error('Unsupported landing cache version')
    if (!Number.isFinite(payload.stored_at)) throw new Error('Landing cache missing stored_at')
    if (Date.now() - payload.stored_at > LANDING_CACHE_MAX_AGE_MS) {
      localStorage.removeItem(landingCacheKey())
      return null
    }
    const cachedScenarios = payload.scenarios.map(parseVisualGameScenarioListItem)
    return {
      scenarios: cachedScenarios,
      videosByScenarioId: cachedScenarioVideos(cachedScenarios, payload.videos_by_scenario_id),
      previewVideosByScenarioId: cachedScenarioVideos(cachedScenarios, payload.preview_videos_by_scenario_id),
      carouselItems: Array.isArray(payload.carousel_items) ? payload.carousel_items : [],
    }
  } catch {
    localStorage.removeItem(landingCacheKey())
    return null
  }
}

function writeLandingCache(data) {
  try {
    localStorage.setItem(landingCacheKey(), JSON.stringify({
      version: 1,
      locale: locale.value,
      stored_at: Date.now(),
      scenarios: data.scenarios,
      videos_by_scenario_id: data.videosByScenarioId,
      preview_videos_by_scenario_id: data.previewVideosByScenarioId,
      carousel_items: data.carouselItems,
    }))
  } catch {
    // 浏览器禁止或写满本地存储时，页面仍使用本次网络结果。
  }
}

function landingCacheKey() {
  return `${LANDING_CACHE_KEY}.${locale.value}`
}

function cachedScenarioVideos(cachedScenarios, videosByScenario) {
  return Object.fromEntries(cachedScenarios.map(scenario => {
    const parsed = parseVisualGameScenarioVideoList({ videos: videosByScenario[scenario.id] })
    return [scenario.id, parsed.videos]
  }))
}

function showcaseContentSearchText(item) {
  return item.searchText.toLocaleLowerCase(locale.value)
}

function groupStoryCards(availableScenarios, videosByScenario) {
  const groups = new Map()
  for (const scenario of availableScenarios) {
    const key = scenario.series_id === null ? `scenario:${scenario.id}` : `series:${scenario.series_id}`
    const existing = groups.get(key)
    if (existing) existing.scenarios.push(scenario)
    else groups.set(key, { key, scenarios: [scenario] })
  }
  return [...groups.values()].map(group => storyCardFromGroup(group, videosByScenario))
}

function storyCardFromGroup(group, videosByScenario) {
  const orderedScenarios = [...group.scenarios].sort((left, right) => scenarioEpisodeNo(left) - scenarioEpisodeNo(right))
  const first = orderedScenarios[0]
  const firstPlayable = orderedScenarios.find(scenarioIsPlayable) || null
  const videos = orderedScenarios.flatMap(scenario => videosByScenario[scenario.id])
  const previewVideo = videos.length === 0
    ? null
    : [...videos].sort(compareStoryPreviewVideos)[0]
  const previewVideoHref = previewVideo === null
    ? ''
    : videoSeriesHref(
        previewVideo.scenario_id,
        previewVideo.series_id === null ? `scenario:${previewVideo.scenario_id}` : previewVideo.series_id,
        previewVideo.id,
        previewVideo.season_id === null ? '' : previewVideo.season_id,
      )
  const coverUrl = storyCoverUrl(orderedScenarios)
  const landscapeCoverUrl = storyLandscapeCoverUrl(orderedScenarios)
  return {
    id: group.key,
    title: first.title,
    summary: first.summary || first.tagline,
    tags: [...new Set(orderedScenarios.flatMap(scenario => scenario.tags))],
    episodeCount: orderedScenarios.length,
    endingCount: orderedScenarios.reduce((total, scenario) => total + scenario.ending_count, 0),
    playCount: orderedScenarios.reduce((total, scenario) => total + scenario.play_count, 0),
    updatedAt: Math.max(...orderedScenarios.map(scenarioUpdatedTimestamp)),
    authorName: first.author_name === null ? 'VOKO' : first.author_name,
    videoCount: videos.length,
    coverUrl: coverUrl,
    heroCoverUrl: landscapeCoverUrl,
    thumbCoverUrl: landscapeCoverUrl,
    previewVideoUrl: previewVideo === null ? '' : previewVideo.source_url,
    scenarioIds: orderedScenarios.map(scenario => scenario.id),
    detailHref: `/story/${encodeURIComponent(first.series_id === null ? first.id : first.series_id)}`,
    playState: firstPlayable === null ? 'coming_soon' : 'available',
    playHref: firstPlayable === null ? '' : `/game?${new URLSearchParams({ scenarioId: firstPlayable.id }).toString()}`,
    watchHref: previewVideoHref,
  }
}

function compareStoryPreviewVideos(left, right) {
  return right.play_count - left.play_count
    || showcaseVideoTimestamp(right) - showcaseVideoTimestamp(left)
    || right.id.localeCompare(left.id)
}

function scenarioUpdatedTimestamp(scenario) {
  if (scenario.updated_at === undefined || scenario.updated_at === null) return 0
  const timestamp = Date.parse(String(scenario.updated_at))
  if (!Number.isFinite(timestamp)) throw new Error(`Landing scenario updated_at is invalid: ${scenario.id}`)
  return timestamp
}

function storyLineHrefForStory(story, sourceRuns) {
  const run = sourceRuns
    .filter(item => item.season_id !== null && story.scenarioIds.includes(item.scenario_id))
    .sort((left, right) => Date.parse(right.updated_at) - Date.parse(left.updated_at))[0]
  if (!run) return `/story-tree?${new URLSearchParams({ story: story.id }).toString()}`
  return `/story-tree?${new URLSearchParams({ seasonId: run.season_id }).toString()}`
}

function comparePinnedStories(left, right) {
  const leftRank = pinnedStoryRank(left)
  const rightRank = pinnedStoryRank(right)
  if (leftRank === rightRank) return 0
  return leftRank - rightRank
}

function pinnedStoryRank(story) {
  if (isMistboundStory(story)) return 0
  if (isOdysseyStory(story)) return 1
  if (isPaintedSkinStory(story)) return 2
  return 100
}

function isOdysseyStory(story) {
  return story.id === 'series:odyssey' || story.id === 'scenario:odyssey_chapter_01' || story.title === 'Odyssey'
}

function isMistboundStory(story) {
  return ['series:otome_castle', 'scenario:otome_castle_hot_spring'].includes(story.id)
}

function isPaintedSkinStory(story) {
  return ['series:painted_skin', 'scenario:painted_skin_room'].includes(story.id) || story.title === '画皮'
}

function showcaseVideoTimestamp(video) {
  const timestamp = Date.parse(video.completed_at)
  if (!Number.isFinite(timestamp)) {
    throw new Error(`Landing video has invalid completed_at: ${String(video.completed_at)}, id: ${video.id}`)
  }
  return timestamp
}

function matchesPublishedWithin(item) {
  const publishedAt = showcaseVideoTimestamp(item.video)
  const now = Date.now()
  switch (publishedWithin.value) {
    case 'all':
      return true
    case 'today': {
      const start = new Date()
      start.setHours(0, 0, 0, 0)
      return publishedAt >= start.getTime()
    }
    case 'week':
      return publishedAt >= now - 7 * 24 * 60 * 60 * 1000
    case 'month':
      return publishedAt >= now - 30 * 24 * 60 * 60 * 1000
    default:
      throw new Error(`Unhandled published time filter: ${publishedWithin.value}`)
  }
}

function matchesDramaFilter(item) {
  if (dramaFilter.value === 'all') return true
  const itemDramaId = item.scenario.series_id === null ? `scenario:${item.scenario.id}` : `series:${item.scenario.series_id}`
  return itemDramaId === dramaFilter.value
}

function showcaseCardFromData(scenario, video) {
  if (video.ending_title === null || video.ending_title === undefined || video.ending_title.length === 0) {
    throw new Error(`Landing video missing ending_title, id: ${video.id}`)
  }
  const dramaTitle = scenario.series_title === null ? scenario.title : scenario.series_title
  return {
    scenario,
    video,
    title: video.video_title,
    dramaTitle,
    levelTitle: scenario.title,
    synopsis: video.video_summary,
    endingTitle: video.ending_title,
    searchText: [dramaTitle, scenario.title, video.video_title, video.video_summary, video.ending_title, video.player_name].join(' '),
    completedAtLabel: new Date(showcaseVideoTimestamp(video)).toISOString().slice(0, 10),
  }
}

function groupSeasonCards(items) {
  const groups = new Map()
  for (const item of items) {
    const key = item.video.season_id === null ? `video:${item.video.id}` : `season:${item.video.season_id}`
    const group = groups.get(key)
    if (group) group.push(item)
    else groups.set(key, [item])
  }
  return [...groups.entries()].flatMap(([key, seasonItems]) => {
    const orderedItems = [...seasonItems].sort((left, right) => {
      return scenarioEpisodeNo(left.scenario) - scenarioEpisodeNo(right.scenario)
    })
    const previewItem = orderedItems[0]
    if (previewItem.video.season_id !== null && scenarioEpisodeNo(previewItem.scenario) !== 1) return []
    const latestItem = [...orderedItems].sort((left, right) => {
      return showcaseVideoTimestamp(right.video) - showcaseVideoTimestamp(left.video)
    })[0]
    return [{
      ...previewItem,
      id: key,
      items: orderedItems,
      title: previewItem.video.season_title === null
        ? previewItem.video.video_title
        : previewItem.video.season_title,
      video: {
        ...previewItem.video,
        completed_at: latestItem.video.completed_at,
        play_count: orderedItems.reduce((total, item) => total + item.video.play_count, 0),
        like_count: orderedItems.reduce((total, item) => total + item.video.like_count, 0),
      },
      levelTitle: orderedItems.map(item => item.levelTitle).join(' / '),
      synopsis: previewItem.video.season_summary === null
        ? orderedItems.map(item => item.synopsis).join(' ')
        : previewItem.video.season_summary,
      searchText: [
        previewItem.video.season_title === null ? '' : previewItem.video.season_title,
        previewItem.video.season_summary === null ? '' : previewItem.video.season_summary,
        ...orderedItems.map(item => item.searchText),
      ].join(' '),
    }]
  })
}

function scenarioEpisodeNo(scenario) {
  return scenario.episode_no === null ? 1 : scenario.episode_no
}

function scenarioIsPlayable(scenario) {
  switch (scenario.play_state) {
    case 'available':
      return true
    case 'coming_soon':
      return false
    default:
      throw new Error(`Unhandled scenario play state: ${scenario.play_state}, id: ${scenario.id}`)
  }
}

function selectStoryChannel(id) {
  selectedStoryChannel.value = id
  storyChannelMoreOpen.value = false
}

function closeStoryChannelMoreOnOutsidePress(event) {
  if (!storyChannelMoreOpen.value || storyChannelMore.value?.contains(event.target)) return
  storyChannelMoreOpen.value = false
}

function closeStoryChannelMoreOnEscape(event) {
  if (event.key === 'Escape') storyChannelMoreOpen.value = false
}

function clearShowcaseFilters() {
  query.value = ''
  dramaFilter.value = 'all'
  publishedWithin.value = 'all'
}

function openLogin(target = '/game') {
  topNav.value.openLogin(target)
}

function hasAccountSession() {
  return !!api.tokenStore?.getAccessToken?.()
}

function requestProtectedTarget(event, href) {
  if (!href) {
    event.preventDefault()
    return
  }
  if (hasAccountSession()) return
  event.preventDefault()
  openLogin(href)
}

function requestStoryPlay(event, href) {
  requestProtectedTarget(event, href)
}

function requestStoryLine(event, href) {
  requestProtectedTarget(event, href)
}

function trackContentClick({ contentType, contentId, contentTitle, surface, position, destination }) {
  analytics.track('content_click', {
    content_type: contentType,
    content_id: contentId,
    content_title: contentTitle,
    surface,
    position,
    destination,
  })
}

function trackCarouselClick(destination, contentType = 'game') {
  trackContentClick({
    contentType,
    contentId: activeTopSlideData.value.id,
    contentTitle: activeTopSlideData.value.title,
    surface: 'homepage_carousel',
    position: activeTopSlide.value + 1,
    destination,
  })
}

function trackStoryCardClick(story, position, destination) {
  if (!destination) return
  trackContentClick({
    contentType: 'game',
    contentId: story.id,
    contentTitle: story.title,
    surface: 'homepage_game',
    position,
    destination,
  })
}

function trackSectionMore(contentType, surface, destination) {
  trackContentClick({
    contentType,
    contentId: 'all',
    contentTitle: t('visualGame.landingIntro.viewMore'),
    surface,
    position: 1,
    destination,
  })
}

</script>

<template>
  <main class="landing">
    <SiteTopNav ref="topNav" mobile-drawer />

    <section
      class="top-showcase"
      :class="{ 'is-video-open': officialVideoOpen }"
      :aria-label="t('visualGame.landingCarousel.controls')"
      @touchstart.passive="startTopSlideTouch"
      @touchmove.passive="moveTopSlideTouch"
      @touchend.passive="endTopSlideTouch"
      @touchcancel.passive="cancelTopSlideTouch"
    >
      <template v-if="topSlides.length > 0">
      <div v-if="isMobileLanding && !officialVideoOpen" class="top-showcase-mobile-viewport" :class="{ 'is-dragging': topSlideDragging, 'is-transition-disabled': !topSlideTrackTransitionEnabled }">
        <div class="top-showcase-mobile-track" :style="topSlideTrackStyle">
          <article v-for="(slide, trackIndex) in topSlideTrackSlides" :key="`${slide.id}-${trackIndex}`" class="top-showcase-mobile-slide">
            <MediaImage class="top-showcase-cover" :src="slide.coverUrl" sizes="192px" :max_width="480" :loading="trackIndex === topSlideTrackPosition ? 'eager' : 'lazy'" :fetchpriority="trackIndex === topSlideTrackPosition ? 'high' : 'auto'" alt="" aria-hidden="true" />
            <video
              v-if="slide.kind === 'story' && slide.previewVideoUrl && trackIndex === topSlideTrackPosition && heroPreviewEnabled"
              class="top-showcase-preview"
              :src="slide.previewVideoUrl"
              v-media-poster.hero="slide.coverUrl"
              muted
              autoplay
              playsinline
              preload="metadata"
              aria-hidden="true"
            ></video>
            <a
              v-if="slide.kind === 'story'"
              class="top-showcase-mobile-link"
              :href="slide.detailHref"
              :aria-label="slide.title"
              @click="event => { trackContentClick({ contentType: 'game', contentId: slide.id, contentTitle: slide.title, surface: 'homepage_carousel', position: activeTopSlide + 1, destination: slide.detailHref }); openTopSlideDetail(event) }"
            ></a>
            <button
              v-else
              class="top-showcase-mobile-link top-showcase-mobile-video-trigger"
              type="button"
              :aria-label="t('visualGame.landing.officialVideo')"
              @click="event => { trackCarouselClick('youtube_embed', 'drama'); openOfficialVideoFromSlide(event) }"
            ></button>
            <div class="top-showcase-content" :class="{ 'is-story': slide.kind === 'story' }">
              <p>{{ slide.eyebrow }}</p>
              <h1>{{ slide.title }}</h1>
              <span>{{ slide.summary }}</span>
              <div v-if="slide.kind !== 'story'" class="intro-actions">
                <button class="primary-cta" type="button" @click="openOfficialVideo">
                  <VgIcon name="play" :size="14" />
                  <span>{{ t('visualGame.landing.officialVideo') }}</span>
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>
      <div v-if="!isMobileLanding || officialVideoOpen" class="top-showcase-desktop-layer">
      <MediaImage
        v-if="activeTopSlideData.kind === 'platform-video' && !officialVideoOpen"
        class="top-showcase-cover"
        :src="activeTopSlideData.desktopCoverUrl" sizes="100vw" :max_width="2560" loading="eager" fetchpriority="high" alt=""
        aria-hidden="true"
      />
      <div v-if="activeTopSlideData.kind === 'platform-video' && officialVideoOpen" class="top-showcase-player">
        <iframe
          :src="activeTopSlideData.playerEmbedUrl"
          :title="activeTopSlideData.title"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        ></iframe>
        <button type="button" :aria-label="t('visualGame.actions.cancel')" @click="closeOfficialVideo">
          <VgIcon name="close" :size="22" />
        </button>
      </div>
      <MediaImage
        v-else-if="activeTopSlideData.kind === 'story'"
        class="top-showcase-cover"
        :src="activeTopSlideData.desktopCoverUrl" sizes="100vw" :max_width="2560" loading="eager" fetchpriority="high" alt=""
        aria-hidden="true"
      />
      <video
        v-if="activeTopSlideData.kind === 'story' && activeTopSlideData.previewVideoUrl && heroPreviewEnabled"
        :key="activeTopSlideData.id"
        class="top-showcase-preview"
        :src="activeTopSlideData.previewVideoUrl"
        v-media-poster.hero="activeTopSlideData.desktopCoverUrl"
        muted
        autoplay
        playsinline
        preload="metadata"
        aria-hidden="true"
      ></video>
      <a
        v-if="activeTopSlideData.kind === 'story'"
        class="top-showcase-slide-link"
        :href="activeTopSlideData.detailHref"
        :aria-label="activeTopSlideData.title"
        data-testid="carousel-detail-link"
        @click="event => { trackCarouselClick(activeTopSlideData.detailHref, 'game'); openTopSlideDetail(event) }"
      ></a>
      <div v-if="activeTopSlideData.kind === 'story' || !officialVideoOpen" class="top-showcase-content" :class="{ 'is-story': activeTopSlideData.kind === 'story' }">
        <p>{{ activeTopSlideData.eyebrow }}</p>
        <h1>{{ activeTopSlideData.title }}</h1>
        <span>{{ activeTopSlideData.summary }}</span>
        <div v-if="activeTopSlideData.kind === 'story'" class="intro-actions">
          <a v-if="activeTopSlideData.playHref" class="primary-cta" :href="activeTopSlideData.playHref" data-testid="carousel-start-game" @click="event => { trackCarouselClick(activeTopSlideData.playHref, 'game'); requestStoryPlay(event, activeTopSlideData.playHref) }">
            <VgIcon name="gamepad" :size="16" />
            <span>{{ t('visualGame.landingIntro.start') }}</span>
          </a>
          <a v-if="activeTopSlideData.storyLineHref" class="secondary-cta" :href="activeTopSlideData.storyLineHref" @click="event => { trackCarouselClick(activeTopSlideData.storyLineHref, 'game'); requestStoryLine(event, activeTopSlideData.storyLineHref) }">
            <VgIcon name="map" :size="16" />
            <span>{{ t('visualGame.landingIntro.storyLine') }}</span>
          </a>
          <a class="secondary-cta" :href="activeTopSlideData.watchHref" @click="trackCarouselClick(activeTopSlideData.watchHref, 'drama')">
            <VgIcon name="play" :size="16" />
            <span>{{ t('visualGame.landing.watchDrama') }}</span>
          </a>
        </div>
        <div v-else class="intro-actions">
          <button class="primary-cta" type="button" @click="() => { trackCarouselClick('youtube_embed', 'drama'); openOfficialVideo() }">
            <VgIcon name="play" :size="14" />
            <span>{{ t('visualGame.landing.officialVideo') }}</span>
          </button>
        </div>
      </div>
      </div>
      <span v-if="!officialVideoOpen" class="top-showcase-counter">{{ activeTopSlide + 1 }}/{{ topSlides.length }}</span>
      <div v-if="!officialVideoOpen" class="top-showcase-dots" :aria-label="t('visualGame.landingCarousel.controls')">
        <button v-for="(slide, index) in topSlides" :key="slide.id" type="button" :class="{ 'is-active': index === activeTopSlide }" v-media-background.thumb="slide.thumbCoverUrl" style="background-image: var(--media-image)" :aria-label="t('visualGame.landingCarousel.goToSlide', { count: index + 1 })" @click="showTopSlide(index)">
          <span>{{ slide.title }}</span>
        </button>
      </div>
      </template>
    </section>

    <section class="story-discovery-section" :aria-label="t('visualGame.landingCinema.featuredDramas')">
      <header class="story-discovery-header">
        <h2>{{ t('visualGame.landingCinema.featuredDramas') }}</h2>
        <div class="story-discovery-tools">
          <label>
            <span class="sr-only">{{ t('visualGame.landing.searchLabel') }}</span>
            <input v-model="storyQuery" type="search" :placeholder="t('visualGame.landing.searchPlaceholder')">
          </label>
          <a class="landing-section-arrow" href="/stories" :aria-label="t('visualGame.landingIntro.viewMore')" @click="trackSectionMore('game', 'homepage_game', '/stories')">
            <VgIcon name="back" :size="18" />
          </a>
        </div>
      </header>
      <div class="story-discovery-body">
        <div class="story-discovery-controls">
        <nav class="story-channel-rail" :aria-label="t('visualGame.landingCinema.featuredDramas')">
          <button
            v-for="channel in storyChannels"
            :key="channel.id"
            type="button"
            :class="{ 'is-active': selectedStoryChannel === channel.id, 'is-overflow': channel.overflow }"
            @click="selectStoryChannel(channel.id)"
          >
            {{ channel.label }}
          </button>
          <div v-if="overflowStoryChannels.length > 0" ref="storyChannelMore" class="story-channel-more">
            <button
              type="button"
              :class="{ 'is-active': selectedOverflowChannel !== null }"
              aria-controls="story-channel-more-menu"
              :aria-expanded="storyChannelMoreOpen"
              data-testid="story-channel-more"
              @click="storyChannelMoreOpen = !storyChannelMoreOpen"
            >
              <span>{{ selectedOverflowChannel === null ? t('visualGame.landing.moreTags') : selectedOverflowChannel.label }}</span>
              <i aria-hidden="true"></i>
            </button>
            <div v-if="storyChannelMoreOpen" id="story-channel-more-menu" class="story-channel-more-menu">
              <button
                v-for="channel in overflowStoryChannels"
                :key="channel.id"
                type="button"
                :class="{ 'is-active': selectedStoryChannel === channel.id }"
                @click="selectStoryChannel(channel.id)"
              >
                {{ channel.label }}
              </button>
            </div>
          </div>
        </nav>
        </div>

        <div class="story-discovery-grid">
        <aside class="landing-section landing-stories-section" :aria-label="t('visualGame.landingCinema.featuredDramas')">
      <div v-if="loading" class="story-catalog-grid" aria-hidden="true">
        <article v-for="item in 2" :key="item" class="story-card story-card-skeleton">
          <span></span>
          <i></i>
          <b></b>
        </article>
      </div>
      <div v-else-if="error" class="story-catalog-empty">
        <p>{{ error }}</p>
        <button type="button" @click="loadLanding">{{ t('visualGame.landing.retry') }}</button>
      </div>
      <div v-else-if="landingStorySections.length" class="story-list-stack">
        <section
          v-for="section in landingStorySections"
          :key="section.id"
          class="story-list-section"
          :class="{ 'is-mobile-only': section.mobileOnly }"
        >
          <header class="story-list-header">
            <h3>{{ section.title }}</h3>
            <a href="/stories" :aria-label="t('visualGame.landingIntro.viewMore')" @click="trackSectionMore('game', 'homepage_game', '/stories')">
              <VgIcon name="back" :size="18" />
            </a>
          </header>
          <div class="story-poster-grid">
            <component
              v-for="(story, index) in section.items.slice(0, 10)"
              :key="story.id"
              :is="story.playState === 'available' ? 'a' : 'article'"
              class="story-poster"
              :class="[index === 0 ? 'is-left' : 'is-right', { 'is-disabled': story.playState !== 'available' }]"
              :href="story.playState === 'available' ? (isMobileLanding ? story.detailHref : story.storyLineHref) : undefined"
              :aria-label="story.title"
              @click="trackStoryCardClick(story, index + 1, story.playState === 'available' ? (isMobileLanding ? story.detailHref : story.storyLineHref) : '')"
              @mouseenter="startStoryPreview($event, story, `${section.id}:${story.id}`)"
              @mouseleave="story.previewVideoUrl ? stopStoryPreview($event) : undefined"
              @focusin="startStoryPreview($event, story, `${section.id}:${story.id}`)"
              @focusout="story.previewVideoUrl ? stopStoryPreview($event) : undefined"
            >
              <MediaImage sizes="(max-width: 680px) 40vw, (max-width: 980px) 33vw, 20vw" :max_width="960" v-if="story.coverUrl && !storyPreviewIsActive(`${section.id}:${story.id}`)" :src="story.coverUrl" :alt="story.title" loading="lazy" decoding="async" />
              <video
                v-if="storyPreviewIsActive(`${section.id}:${story.id}`)"
                :src="story.previewVideoUrl"
                v-media-poster="story.coverUrl"
                muted
                playsinline
                preload="metadata"
              ></video>
              <span v-else-if="!story.coverUrl" class="story-poster-fallback" aria-hidden="true">{{ story.title.slice(0, 1) }}</span>
              <span class="story-poster-copy">
                <strong>{{ story.title }}</strong>
                <span class="story-poster-meta">
                  <span :aria-label="`${t('visualGame.landingMetrics.playCount')} ${story.playCount}`"><VgIcon name="fire" :size="12" />{{ story.playCount }}</span>
                  <span>{{ story.authorName }}</span>
                </span>
                <small>{{ story.summary || t('visualGame.landing.scenarioFallback') }}</small>
              </span>
              <span v-if="story.playState !== 'available'" class="story-poster-coming-soon">
                {{ t('visualGame.landing.comingSoon') }}
              </span>
            </component>
          </div>
        </section>
      </div>
      <div v-else class="story-catalog-empty">{{ t('visualGame.landing.emptyGames') }}</div>
        </aside>
      </div>
      </div>
    </section>

    <LandingLiveRooms />

    <section class="landing-section landing-drama-section" aria-labelledby="landing-drama-title">
      <header class="landing-section-header">
        <h2 id="landing-drama-title">{{ t('visualGame.landingCinema.playerStories') }}</h2>
        <a class="landing-section-arrow" :href="videosMoreHref" :aria-label="t('visualGame.landingIntro.viewMore')" @click="trackSectionMore('drama', 'homepage_drama', videosMoreHref)">
          <VgIcon name="back" :size="18" />
        </a>
      </header>
      <section v-if="loading" class="landing-skeleton-grid" aria-hidden="true">
        <div v-for="item in 6" :key="item">
          <i></i>
          <span></span>
          <span></span>
        </div>
      </section>
      <section v-else-if="error" class="showcase-status" role="alert">
        <p>{{ error }}</p>
        <button type="button" @click="loadLanding">{{ t('visualGame.landing.retry') }}</button>
      </section>
      <template v-else>
        <ShowcaseVideoList
          v-if="visibleShowcaseVideos.length > 0"
          class="landing-drama-list"
          :groups="landingDramaGroups"
          :initial-filters="{ query, sort, drama: dramaFilter, published: publishedWithin }"
          :show-controls="false"
          compact
          mobile-dramas-navigation
          analytics-surface="homepage_drama"
        />
        <div v-else class="showcase-empty">
          <VgIcon name="search" :size="28" />
          <h3>{{ t('visualGame.landing.noSearchResults') }}</h3>
          <button type="button" @click="clearShowcaseFilters">{{ t('visualGame.landing.clearSearch') }}</button>
        </div>
      </template>
    </section>

    <footer class="landing-footer">
      <div class="footer-brand">
        <div class="brand"><strong>voko.world</strong><small>{{ t('visualGame.productBrandSub') }}</small></div>
        <p>{{ t('visualGame.landing.footer') }}</p>
      </div>
      <div class="footer-group">
        <strong>{{ t('visualGame.landing.footerSocial') }}</strong>
        <a :href="footerLinks.facebook" target="_blank" rel="noopener noreferrer">Facebook</a>
        <a :href="footerLinks.x" target="_blank" rel="noopener noreferrer">X</a>
        <a :href="footerLinks.youtube" target="_blank" rel="noopener noreferrer">YouTube</a>
        <a :href="footerLinks.instagram" target="_blank" rel="noopener noreferrer">Instagram</a>
        <a :href="footerLinks.tiktok" target="_blank" rel="noopener noreferrer">TikTok</a>
        <a :href="footerLinks.discord" target="_blank" rel="noopener noreferrer">Discord</a>
      </div>
      <div class="footer-group">
        <strong>{{ t('visualGame.landing.footerLegal') }}</strong>
        <a :href="footerLinks.privacy">{{ t('visualGame.landing.privacy') }}</a>
        <a :href="footerLinks.terms">{{ t('visualGame.landing.terms') }}</a>
        <a :href="footerLinks.contact">{{ t('visualGame.landing.contact') }}</a>
      </div>
    </footer>

  </main>
</template>

<style scoped>
.landing { min-height: 100vh; overflow-x: clip; background: var(--drama-color-bg); color: var(--drama-color-text); }
footer a { color: var(--drama-color-text-muted); font-size: 13px; font-weight: var(--drama-font-weight-medium); letter-spacing: .1em; text-decoration: none; transition: color .2s ease; }
footer a:hover { color: var(--drama-color-primary); }
.landing-section { display: grid; align-content: start; gap: clamp(var(--drama-space-lg), 2.4vw, 34px); padding: clamp(44px, 4vw, 64px) clamp(var(--drama-space-xl), 6vw, 96px); border-top: 1px solid color-mix(in srgb, var(--drama-color-border) 72%, transparent); }
.landing-stories-section { padding: 32px clamp(24px, 3vw, 48px) 40px; border-top: 0; background: var(--drama-color-bg); }
.landing-drama-section { padding: 32px clamp(24px, 3vw, 48px) 40px; border-top: 0; background: var(--drama-color-surface); }
.landing-stories-section .landing-section-header,
.landing-drama-section .landing-section-header { padding-bottom: 0; border-bottom: 0; }
.landing-stories-section .landing-section-header h2,
.landing-drama-section .landing-section-header h2 { font-family: var(--drama-font-family-body); font-size: clamp(26px, 2.5vw, 32px); font-weight: var(--drama-font-weight-bold); letter-spacing: -.025em; }
.landing-drama-section :deep(.showcase-video-list) { gap: var(--drama-space-xl); }
.landing-drama-section :deep(.showcase-controls) { position: sticky; z-index: 20; top: 56px; flex-wrap: wrap; align-items: center; gap: 8px; padding: 10px 0 14px; background: color-mix(in srgb, var(--drama-color-surface) 96%, transparent); backdrop-filter: blur(14px); }
.landing-drama-section :deep(.showcase-controls label:first-child) { width: min(100%, 360px); min-width: min(100%, 260px); flex: 0 1 360px; }
.landing-drama-section :deep(.showcase-controls input) { width: 100%; height: 36px; padding-inline: 12px; border-radius: 8px; background: var(--drama-color-surface); font-size: 12px; }
.landing-drama-section :deep(.showcase-controls .script-filter),
.landing-drama-section :deep(.showcase-controls .publish-filter) { gap: 6px; font-size: 11px; }
.landing-drama-section :deep(.script-filter-select) { min-width: 148px; }
.landing-drama-section :deep(.publish-filter-select) { min-width: 126px; }
.landing-drama-section :deep(.showcase-controls .drama-select) { height: 36px; border-radius: 8px; background: var(--drama-color-surface); font-size: 12px; }
.landing-drama-section :deep(.sort-tabs) { margin-left: auto; padding: 2px; border-radius: var(--drama-radius-full); background: var(--drama-color-surface-alt); }
.landing-drama-section :deep(.sort-tabs button) { height: 32px; padding-inline: 14px; border: 0; border-radius: var(--drama-radius-full); font-size: 12px; }
.landing-drama-section :deep(.sort-tabs button.is-active) { background: var(--drama-color-primary); color: var(--drama-color-on-primary); }
.landing-section-header { display: flex; align-items: center; justify-content: space-between; gap: var(--drama-space-xl); padding-bottom: clamp(var(--drama-space-md), 1.8vw, var(--drama-space-xl)); border-bottom: 1px solid var(--drama-color-border); }
.landing-section-header h2 { margin: 0; font-family: var(--vgs-font-serif); font-size: clamp(25px, 2.4vw, 36px); font-weight: 500; letter-spacing: -.025em; line-height: 1.12; }
.landing-section-arrow { display: grid; width: 32px; height: 32px; flex: none; place-items: center; border-radius: var(--drama-radius-full); color: var(--drama-color-text-muted); text-decoration: none; transition: background 160ms ease, color 160ms ease, transform 160ms ease; }
.landing-section-arrow :deep(.vg-icon) { transform: rotate(180deg); }
.landing-section-arrow:hover, .landing-section-arrow:focus-visible { background: var(--drama-color-surface-alt); color: var(--drama-color-text); transform: translateX(2px); }
.story-poster-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: clamp(var(--drama-space-md), 1.6vw, var(--drama-space-xl)); }
.story-poster { position: relative; display: grid; aspect-ratio: 9 / 16; min-height: 0; overflow: hidden; align-items: end; padding: clamp(18px, 1.8vw, 24px); border: 1px solid color-mix(in srgb, var(--drama-color-border) 82%, transparent); border-radius: var(--drama-radius-md); background: radial-gradient(circle at 50% 30%, color-mix(in srgb, var(--drama-color-primary) 20%, transparent), transparent 38%), var(--drama-color-surface-alt); color: var(--drama-color-on-primary); text-decoration: none; isolation: isolate; }
.story-poster.is-right { text-align: left; }
.story-poster::before { position: absolute; z-index: 1; inset: 0; background: linear-gradient(180deg, transparent 36%, color-mix(in srgb, var(--drama-color-text) 82%, transparent)); content: ''; pointer-events: none; transition: background .28s ease; }
.story-poster::after { position: absolute; z-index: 2; inset: 10px; border: 1px solid color-mix(in srgb, var(--drama-color-primary) 48%, transparent); content: ''; opacity: .42; pointer-events: none; transition: opacity .28s ease, transform .28s ease; }
.story-poster > img, .story-poster > video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; filter: brightness(1.04) saturate(1); transition: transform .5s ease, filter .35s ease; }
.story-poster:hover > img, .story-poster:focus-visible > img, .story-poster:hover > video, .story-poster:focus-visible > video { filter: brightness(1.02) saturate(1); transform: scale(1.045); }
.story-poster:hover::after, .story-poster:focus-visible::after { opacity: .95; transform: scale(.985); }
.story-poster-copy { position: relative; z-index: 3; display: grid; max-width: min(600px, 92%); gap: 7px; transform: translateY(0); transition: transform .28s ease; }
.story-poster:hover .story-poster-copy, .story-poster:focus-visible .story-poster-copy { transform: translateY(-8px); }
.story-poster-copy strong { color: var(--drama-color-on-primary); font-family: var(--vgs-font-serif); font-size: clamp(22px, 2.1vw, 30px); font-weight: 500; letter-spacing: -.035em; line-height: 1.05; text-shadow: 0 8px 30px color-mix(in srgb, var(--drama-color-text) 82%, transparent); }
.story-poster-copy small { display: -webkit-box; overflow: hidden; color: var(--drama-color-surface-alt); font-size: clamp(12px, 1vw, 14px); line-height: 1.55; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.story-poster-coming-soon { position: absolute; z-index: 4; inset: 0; display: grid; place-items: center; background: color-mix(in srgb, var(--drama-color-bg) 68%, transparent); color: var(--drama-color-text); font-family: var(--vgs-font-serif); font-size: clamp(22px, 2.4vw, 36px); letter-spacing: .08em; opacity: 0; pointer-events: none; text-transform: uppercase; transition: opacity .24s ease; }
.story-poster.is-disabled:hover .story-poster-coming-soon, .story-poster.is-disabled:focus-within .story-poster-coming-soon { opacity: 1; }
.story-poster-fallback { position: absolute; inset: 0; display: grid; place-items: center; color: color-mix(in srgb, var(--drama-color-primary) 72%, transparent); font-family: var(--vgs-font-serif); font-size: clamp(92px, 16vw, 220px); }
.intro-hero { position: relative; display: grid; height: clamp(420px, 48vw, 580px); overflow: hidden; place-items: center; padding: clamp(40px, 5vw, 68px) clamp(64px, 7vw, 112px); background: var(--drama-color-surface-alt); isolation: isolate; }
.intro-hero::after { position: absolute; z-index: 1; inset: 0; background: linear-gradient(90deg, color-mix(in srgb, var(--drama-color-text) 72%, transparent), color-mix(in srgb, var(--drama-color-text) 18%, transparent) 62%, transparent); content: ''; pointer-events: none; }
.intro-hero::before { position: absolute; z-index: -2; inset: 0; background-image: linear-gradient(color-mix(in srgb, var(--drama-color-text) 3%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--drama-color-text) 3%, transparent) 1px, transparent 1px); background-size: clamp(52px, 5vw, 76px) clamp(52px, 5vw, 76px); content: ''; mask-image: linear-gradient(90deg, transparent, black 22% 86%, transparent); pointer-events: none; }
.intro-hero-video { position: absolute; z-index: 1; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; opacity: 0; pointer-events: none; transition: opacity .42s ease; }
.intro-hero-video.is-ready { opacity: 1; }
.intro-hero-cover { position: absolute; z-index: 0; inset: 0; background-color: var(--drama-color-surface-alt); background-position: 62% center; background-size: cover; }
.intro-atmosphere { position: absolute; z-index: -1; inset: 0; pointer-events: none; }
.intro-scene-deck { position: absolute; top: 50%; right: clamp(24px, 6vw, 96px); width: min(47vw, 650px); height: min(58vw, 620px); transform: translateY(-50%); }
.intro-scene { position: absolute; display: block; overflow: hidden; aspect-ratio: 16 / 10; border: 1px solid color-mix(in srgb, var(--drama-color-primary) 46%, transparent); background: var(--drama-color-surface); box-shadow: 0 34px 80px color-mix(in srgb, var(--drama-color-bg) 82%, transparent), inset 0 0 50px color-mix(in srgb, var(--drama-color-bg) 54%, transparent); }
.intro-scene::before, .intro-scene::after { position: absolute; z-index: 4; width: 24px; height: 24px; content: ''; }
.intro-scene::before { top: 8px; left: 8px; border-top: 1px solid var(--drama-color-primary); border-left: 1px solid var(--drama-color-primary); }
.intro-scene::after { right: 8px; bottom: 8px; border-right: 1px solid var(--drama-color-primary); border-bottom: 1px solid var(--drama-color-primary); }
.intro-scene em { position: absolute; display: block; }
.intro-scene b { position: absolute; bottom: 12%; left: 50%; width: 24%; height: 44%; border-radius: 42% 42% 8% 8%; background: color-mix(in srgb, var(--drama-color-bg) 90%, transparent); filter: drop-shadow(0 0 18px color-mix(in srgb, var(--drama-color-text) 12%, transparent)); transform: translateX(-50%); }
.intro-scene b::before { position: absolute; top: -23%; left: 50%; width: 46%; aspect-ratio: 1; border-radius: var(--drama-radius-full); background: inherit; content: ''; transform: translateX(-50%); }
.intro-scene s { position: absolute; display: block; background: color-mix(in srgb, var(--drama-color-bg) 88%, transparent); text-decoration: none; }
.intro-scene > span { position: absolute; z-index: 5; display: grid; }
.intro-scene u { display: block; text-decoration: none; }
.intro-scene.is-one { top: 4%; right: 0; width: 39%; background: radial-gradient(circle at 68% 28%, color-mix(in srgb, var(--drama-color-danger) 38%, transparent), transparent 34%), linear-gradient(160deg, var(--drama-color-surface-alt), var(--drama-color-bg)); animation: scene-float-one 7s ease-in-out infinite; }
.intro-scene.is-one em { inset: 11% 0 11% 0; background: linear-gradient(116deg, transparent 0 38%, color-mix(in srgb, var(--drama-color-danger) 34%, transparent) 38% 54%, transparent 54%), linear-gradient(90deg, color-mix(in srgb, var(--drama-color-bg) 72%, transparent), transparent); clip-path: polygon(0 0, 100% 8%, 82% 100%, 0 88%); }
.intro-scene.is-one b { left: 68%; width: 20%; height: 39%; }
.intro-scene.is-one s { bottom: 12%; left: 19%; width: 1px; height: 61%; box-shadow: 18px 0 0 color-mix(in srgb, var(--drama-color-text) 8%, transparent), 36px 0 0 color-mix(in srgb, var(--drama-color-text) 5%, transparent); }
.intro-scene.is-one > span { top: 12px; right: 12px; gap: 5px; width: 30%; }
.intro-scene.is-one u:first-child { height: 3px; background: var(--drama-color-danger); }
.intro-scene.is-one u:last-child { width: 62%; height: 2px; margin-left: auto; background: color-mix(in srgb, var(--drama-color-text) 44%, transparent); }
.intro-scene.is-two { z-index: 2; top: 28%; left: 0; width: 52%; border-color: color-mix(in srgb, var(--drama-color-primary) 76%, transparent); background: radial-gradient(circle at 36% 22%, color-mix(in srgb, var(--drama-color-primary) 38%, transparent), transparent 32%), linear-gradient(155deg, var(--drama-color-surface), var(--drama-color-bg)); animation: scene-float-two 8s 1s ease-in-out infinite; }
.intro-scene.is-two em { top: 15%; bottom: 12%; left: 17%; width: 66%; border: 1px solid color-mix(in srgb, var(--drama-color-primary) 34%, transparent); background: linear-gradient(180deg, color-mix(in srgb, var(--drama-color-text) 12%, transparent), transparent 72%); box-shadow: inset 0 0 40px color-mix(in srgb, var(--drama-color-bg) 72%, transparent); }
.intro-scene.is-two em::before { position: absolute; top: 0; bottom: 0; left: 50%; width: 1px; background: color-mix(in srgb, var(--drama-color-text) 18%, transparent); content: ''; }
.intro-scene.is-two b { bottom: 10%; height: 41%; }
.intro-scene.is-two s { right: 10%; bottom: 12%; width: 18%; height: 1px; background: var(--drama-color-primary); box-shadow: 0 -9px 0 color-mix(in srgb, var(--drama-color-primary) 38%, transparent); }
.intro-scene.is-two > span { right: 9px; bottom: 9px; left: 9px; grid-template-columns: 1fr 1fr; gap: 6px; padding: 7px; border-top: 1px solid color-mix(in srgb, var(--drama-color-primary) 45%, transparent); background: color-mix(in srgb, var(--drama-color-bg) 78%, transparent); backdrop-filter: blur(8px); }
.intro-scene.is-two u { position: relative; height: 22px; border: 1px solid color-mix(in srgb, var(--drama-color-text) 24%, transparent); background: color-mix(in srgb, var(--drama-color-surface) 64%, transparent); }
.intro-scene.is-two u::before { position: absolute; top: 50%; left: 7px; width: 55%; height: 2px; background: color-mix(in srgb, var(--drama-color-text) 48%, transparent); content: ''; transform: translateY(-50%); }
.intro-scene.is-two u:first-child { border-color: var(--drama-color-primary); box-shadow: inset 2px 0 0 var(--drama-color-primary); }
.intro-scene.is-three { right: 0; bottom: 4%; width: 39%; background: radial-gradient(circle at 50% 26%, color-mix(in srgb, var(--drama-color-info) 32%, transparent), transparent 34%), linear-gradient(150deg, var(--drama-color-surface-alt), var(--drama-color-bg)); animation: scene-float-three 7.5s .4s ease-in-out infinite; }
.intro-scene.is-three em { top: 17%; right: 13%; width: 31%; aspect-ratio: 1; border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-info) 42%, var(--drama-color-text)); box-shadow: 0 0 30px color-mix(in srgb, var(--drama-color-info) 28%, transparent); }
.intro-scene.is-three em::after { position: absolute; right: 17%; bottom: -140%; width: 1px; height: 135%; background: color-mix(in srgb, var(--drama-color-info) 28%, transparent); box-shadow: -38px 0 0 color-mix(in srgb, var(--drama-color-info) 14%, transparent); content: ''; }
.intro-scene.is-three b { left: 38%; width: 18%; height: 35%; }
.intro-scene.is-three s { right: 18%; bottom: 12%; width: 15%; height: 29%; border-radius: 42% 42% 8% 8%; }
.intro-scene.is-three s::before { position: absolute; top: -21%; left: 50%; width: 45%; aspect-ratio: 1; border-radius: var(--drama-radius-full); background: inherit; content: ''; transform: translateX(-50%); }
.intro-scene.is-three > span { top: 12px; left: 12px; gap: 5px; width: 30%; }
.intro-scene.is-three u:first-child { height: 3px; background: var(--drama-color-info); }
.intro-scene.is-three u:last-child { width: 62%; height: 2px; background: color-mix(in srgb, var(--drama-color-text) 44%, transparent); }
.intro-branch { position: absolute; right: -5%; bottom: 2%; width: min(48vw, 700px); overflow: visible; color: var(--drama-color-danger); filter: drop-shadow(0 0 8px color-mix(in srgb, var(--drama-color-danger) 46%, transparent)); mask-image: linear-gradient(90deg, transparent, black 7% 78%, transparent); }
.intro-branch path { fill: none; stroke: currentColor; stroke-width: 1.4; stroke-dasharray: 8 5; animation: branch-flow 8s linear infinite; }
.intro-branch .branch-secondary { opacity: .64; stroke-width: 1.15; animation-duration: 10s; }
.intro-branch .branch-ghost { opacity: .3; stroke-width: .9; animation-duration: 13s; }
.intro-branch circle { fill: var(--drama-color-bg); stroke: currentColor; stroke-width: 2; }
.intro-branch .branch-node-secondary { opacity: .55; }
.intro-carousel { position: relative; z-index: 2; display: grid; width: min(100%, 1320px); height: 100%; }
.intro-slide { grid-area: 1 / 1; display: grid; grid-template-columns: minmax(0, 1fr) minmax(280px, 38%); align-items: center; gap: clamp(var(--drama-space-2xl), 7vw, 112px); opacity: 0; pointer-events: none; transform: translateX(22px); transition: opacity .36s ease, transform .36s ease; }
.intro-slide.is-active { opacity: 1; pointer-events: auto; transform: translateX(0); }
.intro-slide.is-intro { grid-template-columns: 1fr; }
.intro-slide.is-story { position: relative; grid-template-columns: minmax(0, 620px); }
.intro-slide.is-story::before,
.intro-slide.is-story::after { position: absolute; z-index: 1; width: 28px; height: 28px; content: ''; pointer-events: none; }
.intro-slide.is-story::before { top: 14px; left: 14px; border-top: 1px solid var(--drama-color-primary); border-left: 1px solid var(--drama-color-primary); }
.intro-slide.is-story::after { right: 14px; bottom: 14px; border-right: 1px solid var(--drama-color-primary); border-bottom: 1px solid var(--drama-color-primary); }
.intro-content { position: relative; z-index: 2; display: grid; justify-items: start; gap: clamp(var(--drama-space-lg), 2.4vw, 34px); }
.intro-eyebrow { margin: 0; color: var(--drama-color-primary); font-size: 12px; letter-spacing: .24em; text-transform: uppercase; }
.intro-hero h1 { max-width: min(100%, 820px); margin: 0; color: var(--drama-color-on-primary); font-family: var(--vgs-font-serif); font-size: clamp(30px, 3.8vw, 52px); font-weight: 500; letter-spacing: -.035em; line-height: 1; text-shadow: 0 18px 54px var(--drama-color-text); }
.intro-slide.is-intro h1 { white-space: nowrap; }
.intro-summary { max-width: 560px; margin: 0; color: var(--drama-color-surface-alt); font-size: clamp(13px, 1.15vw, 16px); line-height: 1.6; }
.intro-carousel-controls { position: absolute; z-index: 4; inset: 0; pointer-events: none; }
.intro-carousel-arrow { position: absolute; top: 50%; display: grid; width: 42px; height: 42px; place-items: center; padding: 0; border: 1px solid color-mix(in srgb, var(--drama-color-on-primary) 52%, transparent); border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-text) 56%, transparent); color: var(--drama-color-on-primary); cursor: pointer; font-size: 24px; pointer-events: auto; transform: translateY(-50%); backdrop-filter: blur(10px); }
.intro-carousel-arrow.is-prev { left: calc(50% - 50vw + clamp(var(--drama-space-md), 1.4vw, var(--drama-space-xl))); }
.intro-carousel-arrow.is-next { right: calc(50% - 50vw + clamp(var(--drama-space-md), 1.4vw, var(--drama-space-xl))); }
.intro-carousel-arrow:hover { border-color: var(--drama-color-primary); color: var(--drama-color-primary); }
.intro-carousel-dots { position: absolute; right: clamp(var(--drama-space-xl), 6vw, 96px); bottom: clamp(var(--drama-space-lg), 3vw, 42px); display: flex; align-items: end; gap: var(--drama-space-sm); pointer-events: auto; }
.intro-carousel-dots button { position: relative; width: clamp(92px, 10vw, 148px); aspect-ratio: 16 / 9; overflow: hidden; padding: 0; border: 1px solid var(--drama-color-border-strong); border-radius: var(--drama-radius-sm); background-color: var(--drama-color-surface); background-position: center; background-size: cover; box-shadow: var(--drama-shadow-sm); color: var(--drama-color-text); cursor: pointer; opacity: .62; transition: border-color .2s ease, opacity .2s ease, transform .2s ease; }
.intro-carousel-dots button::before { position: absolute; inset: 0; background: linear-gradient(180deg, transparent 34%, color-mix(in srgb, var(--drama-color-bg) 86%, transparent)); content: ''; }
.intro-carousel-dots button span { position: absolute; z-index: 1; right: 8px; bottom: 6px; left: 8px; overflow: hidden; font-size: 10px; font-weight: var(--drama-font-weight-medium); line-height: 1.25; text-align: left; text-overflow: ellipsis; white-space: nowrap; }
.intro-carousel-dots button:hover { border-color: var(--drama-color-on-primary); opacity: .9; transform: translateY(-2px); }
.intro-carousel-dots button.is-active { border-color: var(--drama-color-on-primary); opacity: 1; transform: translateY(-4px); }
.intro-actions { display: flex; align-items: flex-start; gap: var(--drama-space-md); }
.continue-cta-group { display: grid; grid-template-columns: repeat(2, max-content); align-items: start; justify-items: center; column-gap: var(--drama-space-md); }
.continue-game-cta { grid-column: 1; grid-row: 1; }
.story-line-cta { grid-column: 2; grid-row: 1; }
.primary-cta, .secondary-cta { display: inline-flex; min-height: 46px; align-items: center; justify-content: center; gap: var(--drama-space-sm); border-radius: var(--drama-radius-full); font-size: 12px; font-weight: var(--drama-font-weight-medium); letter-spacing: .07em; text-decoration: none; transition: transform .2s ease, border-color .2s ease, background .2s ease, box-shadow .2s ease; }
.primary-cta { min-width: 172px; gap: var(--drama-space-sm); padding: 0 var(--drama-space-xl); border: 1px solid var(--drama-color-on-primary); border-radius: var(--drama-radius-full); background: var(--drama-color-on-primary); color: var(--drama-color-text); }
.intro-actions button.primary-cta { cursor: pointer; }
.secondary-cta { min-width: 124px; padding: 0 var(--drama-space-lg); border: 1px solid color-mix(in srgb, var(--drama-color-on-primary) 46%, transparent); background: color-mix(in srgb, var(--drama-color-text) 30%, transparent); color: var(--drama-color-on-primary); backdrop-filter: blur(12px); }
.secondary-cta.is-disabled { opacity: .42; pointer-events: none; }
.primary-cta:hover, .primary-cta:focus-visible, .secondary-cta:hover, .secondary-cta:focus-visible { transform: translateY(-2px); }
.primary-cta:hover, .primary-cta:focus-visible { border-color: var(--drama-color-surface-alt); background: var(--drama-color-surface-alt); }
.secondary-cta:hover, .secondary-cta:focus-visible { border-color: var(--drama-color-on-primary); box-shadow: 0 0 0 3px color-mix(in srgb, var(--drama-color-on-primary) 22%, transparent), 0 0 18px color-mix(in srgb, var(--drama-color-on-primary) 24%, transparent); color: var(--drama-color-on-primary); }
.intro-footer-mark { position: absolute; right: clamp(var(--drama-space-xl), 7vw, 112px); bottom: clamp(var(--drama-space-xl), 5vw, 64px); display: flex; align-items: center; gap: var(--drama-space-sm); }
.intro-footer-mark span { width: 3px; height: 3px; border-radius: var(--drama-radius-full); background: var(--drama-color-text-muted); }
.intro-footer-mark i { display: block; width: clamp(56px, 7vw, 104px); height: 1px; background: linear-gradient(90deg, var(--drama-color-text-muted), transparent); }
@keyframes scene-float-one { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
@keyframes scene-float-two { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(12px); } }
@keyframes scene-float-three { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(8px); } }
@keyframes branch-flow { to { stroke-dashoffset: -52; } }
.story-catalog { padding: clamp(48px, 6vw, 86px) clamp(var(--drama-space-xl), 8vw, 128px); border-top: 1px solid color-mix(in srgb, var(--drama-color-border) 70%, transparent); background: linear-gradient(180deg, var(--drama-color-bg), var(--drama-color-surface)); }
.story-catalog-head { display: grid; gap: var(--drama-space-sm); margin-bottom: clamp(var(--drama-space-xl), 4vw, 52px); }
.story-catalog-head p { margin: 0; color: var(--drama-color-primary); font-size: 12px; letter-spacing: .24em; text-transform: uppercase; }
.story-catalog-head h2 { margin: 0; color: var(--drama-color-text); font-family: var(--vgs-font-serif); font-size: clamp(30px, 3.4vw, 52px); font-weight: 500; letter-spacing: -.035em; line-height: 1.04; }
.story-catalog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: clamp(var(--drama-space-lg), 2.4vw, 34px); }
.story-card { display: grid; grid-template-columns: minmax(220px, .88fr) minmax(0, 1fr); min-height: 310px; overflow: hidden; border: 1px solid color-mix(in srgb, var(--drama-color-border) 86%, transparent); background: color-mix(in srgb, var(--drama-color-surface) 72%, var(--drama-color-bg)); box-shadow: 0 28px 80px color-mix(in srgb, var(--drama-color-bg) 58%, transparent); }
.story-card-cover { position: relative; display: grid; min-height: 100%; overflow: hidden; place-items: center; background: radial-gradient(circle at 42% 22%, color-mix(in srgb, var(--drama-color-primary) 28%, transparent), transparent 34%), linear-gradient(140deg, var(--drama-color-surface-alt), var(--drama-color-bg)); color: var(--drama-color-primary); font-family: var(--vgs-font-serif); font-size: clamp(64px, 8vw, 116px); text-decoration: none; }
.story-card-cover::after { position: absolute; inset: 0; background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--drama-color-bg) 22%, transparent)); content: ''; pointer-events: none; }
.story-card-cover img { width: 100%; height: 100%; object-fit: cover; transition: transform .35s ease, filter .35s ease; }
.story-card:hover .story-card-cover img { filter: brightness(1.06); transform: scale(1.03); }
.story-card-body { display: grid; align-content: space-between; gap: var(--drama-space-lg); padding: clamp(var(--drama-space-xl), 3vw, 42px); }
.story-card-kicker, .story-card-meta { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-sm); color: var(--drama-color-text-muted); font-size: 12px; letter-spacing: .06em; }
.story-card-kicker span, .story-card-meta span { border: 1px solid color-mix(in srgb, var(--drama-color-border) 76%, transparent); padding: 6px 9px; background: color-mix(in srgb, var(--drama-color-bg) 38%, transparent); }
.story-card h3 { margin: 0; font-family: var(--vgs-font-serif); font-size: clamp(26px, 2.4vw, 40px); font-weight: 500; letter-spacing: -.03em; line-height: 1.05; }
.story-card h3 a { color: var(--drama-color-text); text-decoration: none; }
.story-card h3 a:hover { color: var(--drama-color-primary); }
.story-card p { display: -webkit-box; overflow: hidden; margin: 0; color: var(--drama-color-text-muted); font-size: 14px; line-height: 1.75; -webkit-box-orient: vertical; -webkit-line-clamp: 3; }
.story-card-actions { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); }
.story-card-actions a { display: inline-flex; min-height: 42px; align-items: center; justify-content: center; padding: 0 var(--drama-space-lg); border: 1px solid var(--drama-color-border-strong); color: var(--drama-color-text); font-size: 12px; letter-spacing: .1em; text-decoration: none; transition: border-color .2s ease, color .2s ease, background .2s ease, transform .2s ease; }
.story-card-actions a:hover { border-color: var(--drama-color-primary); color: var(--drama-color-primary); transform: translateY(-1px); }
.story-card-actions .story-play-link { border-color: var(--drama-color-primary); background: var(--drama-color-primary); color: var(--drama-color-on-primary); }
.story-card-actions .story-play-link:hover { background: var(--drama-color-primary-hover); color: var(--drama-color-on-primary); }
.story-catalog-empty { padding: var(--drama-space-2xl); border: 1px solid var(--drama-color-border); color: var(--drama-color-text-muted); text-align: center; }
.story-card-skeleton { min-height: 240px; opacity: .35; }
.story-card-skeleton span, .story-card-skeleton i, .story-card-skeleton b { display: block; margin: var(--drama-space-xl); background: color-mix(in srgb, var(--drama-color-text) 10%, transparent); }
.story-card-skeleton span { min-height: 180px; margin: 0; }
.story-card-skeleton i { width: 64%; height: 18px; }
.story-card-skeleton b { width: 42%; height: 12px; }
.showcase-empty button { width: max-content; padding: var(--drama-space-md) var(--drama-space-xl); border: 1px solid var(--drama-color-primary); background: transparent; color: var(--drama-color-primary); cursor: pointer; }
.showcase { padding: clamp(48px, 5vw, 72px) clamp(var(--drama-space-xl), 8vw, 128px) clamp(88px, 8vw, 120px); background: var(--drama-color-surface); }
.showcase-title { margin: 0 0 var(--drama-space-lg); color: var(--drama-color-text); font-family: var(--vgs-font-serif); font-size: clamp(28px, 3vw, 46px); font-weight: 500; letter-spacing: -.03em; line-height: 1.1; }
.showcase-controls { display: flex; flex: 1 1 auto; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: var(--drama-space-md); margin-left: auto; }
.showcase-controls label { flex: 0 0 auto; }
.showcase-controls > label:first-child { width: min(28vw, 340px); }
.showcase-controls input { width: 100%; height: 44px; padding: 0 var(--drama-space-lg); border: 1px solid var(--drama-color-border); background: var(--drama-color-bg); color: var(--drama-color-text); }
.showcase-controls .script-filter,
.showcase-controls .publish-filter { display: flex; flex: 0 0 auto; align-items: center; gap: var(--drama-space-sm); color: var(--drama-color-text-muted); font-size: 12px; }
.script-filter-select { min-width: 180px; }
.publish-filter-select { min-width: 150px; }
.script-filter-select :deep(.drama-select),
.publish-filter-select :deep(.drama-select) { width: 100%; height: 44px; border-color: var(--drama-color-border); border-radius: 0; background: var(--drama-color-bg); }
.sort-tabs { display: flex; gap: var(--drama-space-xs); }
.sort-tabs button { height: 44px; padding: 0 var(--drama-space-lg); border: 1px solid transparent; background: transparent; color: var(--drama-color-text-muted); cursor: pointer; }
.sort-tabs button.is-active { border-color: var(--drama-color-primary); color: var(--drama-color-primary); }
.showcase-results, .showcase-result-group { display: grid; gap: var(--drama-space-2xl); }
.showcase-result-group + .showcase-result-group { padding-top: var(--drama-space-2xl); border-top: 1px solid var(--drama-color-border); }
.player-search-result { display: flex; align-items: center; gap: var(--drama-space-lg); }
.player-search-avatar { display: grid; width: 58px; height: 58px; flex: 0 0 auto; place-items: center; border: 1px solid var(--drama-color-primary); border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-primary) 12%, var(--drama-color-bg)); color: var(--drama-color-primary); font-family: var(--drama-font-family-heading); font-size: var(--drama-font-size-lg); }
.player-search-result > div { display: grid; gap: 5px; }
.player-search-result div a { color: var(--drama-color-text); font-size: var(--drama-font-size-lg); font-weight: var(--drama-font-weight-medium); text-decoration: none; }
.player-search-result div a:hover { color: var(--drama-color-primary); }
.player-search-result div span { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.game-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: var(--drama-space-lg); }
.game-card { display: block; min-width: 0; color: var(--drama-color-text); }
.game-cover { position: relative; display: block; aspect-ratio: 16 / 9; overflow: hidden; border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); text-decoration: none; }
.game-cover video { display: block; width: 100%; height: 100%; object-fit: cover; transition: transform .3s ease, filter .3s ease; }
.game-card:hover .game-cover video, .game-card:focus-visible .game-cover video { filter: brightness(1.06); transform: scale(1.02); }
.game-card-copy { min-width: 0; padding-top: var(--drama-space-md); }
.game-card-copy h3 { display: -webkit-box; overflow: hidden; margin: 0 0 7px; font-family: var(--drama-font-family-body); font-size: 15px; font-weight: var(--drama-font-weight-medium); line-height: 1.4; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.game-card-copy h3 a { color: var(--drama-color-text); text-decoration: none; }
.game-card-copy h3 a:hover { color: var(--drama-color-primary); }
.game-card-context { display: flex; overflow: hidden; align-items: center; justify-content: space-between; gap: var(--drama-space-md); margin: 0 0 6px; color: var(--drama-color-text-muted); font-size: 12px; line-height: 1.4; white-space: nowrap; }
.game-card-context span { overflow: hidden; text-overflow: ellipsis; }
.game-card-context span:first-child { color: var(--drama-color-text); font-weight: var(--drama-font-weight-medium); }
.game-card-context > :last-child { margin-left: auto; }
.game-card-context a { overflow: hidden; margin-left: auto; color: var(--drama-color-text-muted); text-decoration: none; text-overflow: ellipsis; }
.game-card-context a:hover { color: var(--drama-color-primary); }
.game-card-synopsis { position: relative; margin-bottom: var(--drama-space-sm); }
.game-card-synopsis p { display: -webkit-box; min-height: 4.2em; max-height: 4.2em; overflow: hidden; margin: 0; color: var(--drama-color-text-muted); font-size: 11px; line-height: 1.4; -webkit-box-orient: vertical; -webkit-line-clamp: 3; }
.game-card-synopsis p.is-expanded { display: block; min-height: 0; max-height: none; overflow: visible; -webkit-line-clamp: unset; }
.game-card-synopsis button { margin-top: 4px; padding: 0; border: 0; background: transparent; color: var(--drama-color-primary); font-size: 10px; cursor: pointer; }
.game-card-synopsis p button { display: inline; margin: 0 0 0 3px; line-height: inherit; vertical-align: baseline; }
.game-card-synopsis button:hover { color: var(--drama-color-primary-hover); }
.game-card-meta { display: flex; align-items: center; gap: var(--drama-space-md); margin: 0; color: var(--drama-color-text-muted); font-size: 11px; line-height: 1.4; }
.game-card-meta time { margin-left: auto; text-align: right; }
.game-card-metric { position: relative; display: inline-flex; align-items: center; gap: 4px; }
.game-card-metric:hover::after { position: absolute; z-index: 5; bottom: calc(100% + 7px); left: 50%; width: max-content; padding: 5px 8px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); background: var(--drama-color-surface); box-shadow: var(--drama-shadow-sm); color: var(--drama-color-text); content: attr(data-label); font-size: 11px; line-height: 1.2; pointer-events: none; transform: translateX(-50%); }
.showcase-empty { display: grid; min-height: 300px; place-items: center; align-content: center; gap: var(--drama-space-md); color: var(--drama-color-text-muted); text-align: center; }
.showcase-empty h3 { margin: 0; color: var(--drama-color-text); }
.showcase-status { display: grid; min-height: 240px; place-items: center; align-content: center; gap: var(--drama-space-md); color: var(--drama-color-text-muted); text-align: center; }
.showcase-status button { padding: var(--drama-space-md) var(--drama-space-xl); border: 1px solid var(--drama-color-primary); background: transparent; color: var(--drama-color-primary); cursor: pointer; }
.showcase-load-more { width: 100%; height: 1px; }
.landing-skeleton-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--drama-space-lg); opacity: .34; }
.landing-skeleton-grid > div { display: grid; gap: var(--drama-space-md); padding-bottom: var(--drama-space-xl); border: 1px solid var(--drama-color-border); background: var(--drama-color-bg); }
.landing-skeleton-grid i { display: block; aspect-ratio: 16 / 9; background: color-mix(in srgb, var(--drama-color-text) 9%, transparent); }
.landing-skeleton-grid span { display: block; width: 68%; height: 11px; margin-inline: var(--drama-space-xl); border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-text) 11%, transparent); }
.landing-skeleton-grid span:last-child { width: 42%; }
.landing-footer { display: grid; grid-template-columns: minmax(260px, 1fr) auto auto; align-items: start; gap: clamp(var(--drama-space-2xl), 7vw, 112px); padding: clamp(var(--drama-space-2xl), 5vw, 72px) clamp(var(--drama-space-xl), 5vw, 80px); border-top: 1px solid var(--drama-color-border); }
.footer-brand .brand { display: grid; gap: var(--drama-space-sm); }
.footer-brand .brand strong { color: var(--drama-color-text); font-family: var(--drama-font-family-heading); font-size: 15px; letter-spacing: .1em; line-height: 1; }
.footer-brand .brand small { color: var(--drama-color-text-muted); font-size: 12px; letter-spacing: .02em; line-height: 1.5; }
.footer-brand p { max-width: 320px; margin: var(--drama-space-xl) 0 0; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); line-height: 1.7; }
.footer-group { display: grid; min-width: 150px; gap: var(--drama-space-md); }
.footer-group strong { margin-bottom: var(--drama-space-xs); color: var(--drama-color-text); font-size: 12px; letter-spacing: .08em; text-transform: uppercase; }
.footer-group a { width: max-content; color: var(--drama-color-text-muted); font-size: 13px; font-weight: var(--drama-font-weight-normal); letter-spacing: 0; text-decoration: none; transition: color .2s ease; }
.footer-group a:hover { color: var(--drama-color-primary); }
.top-showcase { position: relative; height: clamp(560px, 82svh, 760px); overflow: hidden; background: var(--drama-color-text); isolation: isolate; }
.top-showcase-mobile-viewport { display: none; }
.top-showcase-desktop-layer { position: absolute; inset: 0; }
.top-showcase::after { position: absolute; z-index: 1; inset: 0; background: linear-gradient(90deg, color-mix(in srgb, var(--drama-color-text) 84%, transparent), color-mix(in srgb, var(--drama-color-text) 18%, transparent) 68%, transparent); content: ''; pointer-events: none; }
.top-showcase.is-video-open::after { display: none; }
.top-showcase-cover, .top-showcase-preview { position: absolute; inset: 0; width: 100%; height: 100%; }
.top-showcase-cover { object-position: center; object-fit: cover; }
.top-showcase-preview { object-fit: cover; }
.top-showcase-mobile-link, .top-showcase-counter { display: none; }
.top-showcase-background-video { position: absolute; top: 50%; left: 50%; width: 100vw; height: 56.25vw; min-width: 177.78vh; min-height: 100%; border: 0; pointer-events: none; transform: translate(-50%, -50%); }
.top-showcase-player { position: absolute; z-index: 7; inset: 0; background: var(--drama-color-text); }
.top-showcase-player iframe { width: 100%; height: 100%; border: 0; }
.top-showcase-player button { position: absolute; z-index: 1; top: clamp(var(--drama-space-lg), 3vw, 36px); right: clamp(var(--drama-space-lg), 3vw, 36px); display: grid; width: 44px; height: 44px; place-items: center; padding: 0; border: 1px solid color-mix(in srgb, var(--drama-color-on-primary) 48%, transparent); border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-text) 72%, transparent); box-shadow: var(--drama-shadow-md); color: var(--drama-color-on-primary); cursor: pointer; backdrop-filter: blur(12px); }
.top-showcase-player button:hover, .top-showcase-player button:focus-visible { border-color: var(--drama-color-on-primary); background: var(--drama-color-text); }
.top-showcase-slide-link { position: absolute; z-index: 2; inset: 0; }
.top-showcase-content { position: absolute; z-index: 3; top: 50%; left: clamp(64px, 11vw, 220px); display: grid; width: min(620px, calc(100% - 128px)); gap: clamp(var(--drama-space-lg), 2vw, 30px); color: var(--drama-color-on-primary); transform: translateY(-34%); pointer-events: none; }
.top-showcase-content .intro-actions { pointer-events: auto; }
.top-showcase-content p, .top-showcase-content span { margin: 0; }
/* 轮播底图总压着深色渐变，小标题用浅色：主题主色在亮色主题下是近黑，压在图上看不见 */
.top-showcase-content > p { color: color-mix(in srgb, var(--drama-color-on-primary) 72%, transparent); font-size: 11px; font-weight: var(--drama-font-weight-semibold); letter-spacing: .18em; text-transform: uppercase; }
.top-showcase-content h1 { max-width: 100%; margin: 0; overflow-wrap: anywhere; font-family: var(--vgs-font-serif); font-size: clamp(36px, 3.8vw, 56px); font-weight: 500; letter-spacing: -.045em; line-height: 1.08; text-shadow: 0 18px 54px var(--drama-color-text); }
.top-showcase-content > span { max-width: 560px; color: color-mix(in srgb, var(--drama-color-on-primary) 78%, transparent); font-size: clamp(14px, 1.2vw, 17px); line-height: 1.65; }
.top-showcase-dots { position: absolute; z-index: 5; right: clamp(var(--drama-space-xl), 6vw, 96px); bottom: clamp(var(--drama-space-lg), 3vw, 42px); display: flex; align-items: end; gap: var(--drama-space-sm); }
.top-showcase-dots button { position: relative; width: clamp(92px, 10vw, 148px); aspect-ratio: 16 / 9; overflow: hidden; padding: 0; border: 1px solid var(--drama-color-border-strong); border-radius: var(--drama-radius-sm); background-color: var(--drama-color-surface); background-position: center; background-size: cover; box-shadow: var(--drama-shadow-sm); color: var(--drama-color-on-primary); cursor: pointer; opacity: .62; transition: border-color .2s ease, opacity .2s ease, transform .2s ease; }
.top-showcase-dots button::before { position: absolute; inset: 0; background: linear-gradient(180deg, transparent 34%, color-mix(in srgb, var(--drama-color-bg) 86%, transparent)); content: ''; }
.top-showcase-dots button span { position: absolute; z-index: 1; right: 8px; bottom: 6px; left: 8px; overflow: hidden; font-size: 10px; font-weight: var(--drama-font-weight-medium); line-height: 1.25; text-align: left; text-overflow: ellipsis; white-space: nowrap; }
.top-showcase-dots button:hover { border-color: var(--drama-color-on-primary); opacity: .9; transform: translateY(-2px); }
.top-showcase-dots button.is-active { border-color: var(--drama-color-on-primary); opacity: 1; transform: translateY(-4px); }
.intro-hero { position: relative; display: block; height: auto; min-height: 0; overflow: hidden; padding: clamp(52px, 6vw, 84px) clamp(24px, 6vw, 88px); background: var(--drama-color-surface); isolation: isolate; }
.intro-hero::before { position: absolute; z-index: -1; inset: 0; background: radial-gradient(circle at 72% 38%, color-mix(in srgb, var(--drama-color-primary) 11%, transparent), transparent 30%), linear-gradient(180deg, color-mix(in srgb, var(--drama-color-text) 4%, transparent), transparent); content: ''; mask-image: none; }
.intro-hero::after { display: none; }
.intro-carousel { position: relative; z-index: 1; display: grid; width: min(100%, 1120px); min-height: 340px; height: auto; margin: 0 auto; grid-template-columns: minmax(0, 1fr) minmax(320px, .78fr); align-items: center; gap: clamp(44px, 8vw, 120px); }
.intro-content { position: relative; z-index: 4; display: grid; max-width: 560px; justify-items: start; gap: var(--drama-space-lg); }
.intro-eyebrow { margin: 0; color: var(--drama-color-primary); font-size: 11px; font-weight: var(--drama-font-weight-semibold); letter-spacing: .18em; text-transform: uppercase; }
.intro-hero h1 { max-width: 100%; margin: 0; color: var(--drama-color-text); font-family: var(--vgs-font-serif); font-size: clamp(34px, 4.2vw, 58px); font-weight: 500; letter-spacing: -.045em; line-height: 1; text-shadow: none; }
.intro-summary { display: -webkit-box; max-width: 520px; overflow: hidden; margin: 0; color: var(--drama-color-text-muted); font-size: clamp(14px, 1.2vw, 17px); line-height: 1.65; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.intro-content .intro-actions { margin-top: var(--drama-space-sm); }
.intro-content .primary-cta { min-width: 0; min-height: 42px; padding: 0 var(--drama-space-xl); border-color: var(--drama-color-text); border-radius: var(--drama-radius-md); background: var(--drama-color-text); color: var(--drama-color-bg); }
.intro-content .secondary-cta { min-width: 0; min-height: 42px; border-color: var(--drama-color-border-strong); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); color: var(--drama-color-text); backdrop-filter: none; }
.intro-content .secondary-cta:hover { border-color: var(--drama-color-primary); color: var(--drama-color-primary); }
.intro-content .secondary-cta.is-disabled { color: var(--drama-color-text-muted); }
.intro-deck { position: relative; min-height: 340px; perspective: 1000px; }
.intro-deck-card { --deck-x: calc(var(--deck-offset) * 34px); position: absolute; top: 50%; left: 50%; width: clamp(180px, 18vw, 224px); aspect-ratio: 3 / 4; overflow: hidden; padding: 0; border: 1px solid var(--drama-color-border-strong); border-radius: var(--drama-radius-lg); background: var(--drama-color-surface-alt); box-shadow: var(--drama-shadow-lg); color: var(--drama-color-primary); cursor: pointer; opacity: .54; transform: translate(calc(-50% + var(--deck-x)), -50%) scale(.91) rotate(calc(var(--deck-offset) * 2.5deg)); transform-origin: center bottom; transition: opacity .32s ease, transform .42s cubic-bezier(.2, .8, .2, 1), filter .32s ease; }
.intro-deck-card img { width: 100%; height: 100%; object-fit: cover; }
.intro-deck-card > span { display: grid; width: 100%; height: 100%; place-items: center; font-family: var(--vgs-font-serif); font-size: 72px; }
.intro-deck-card.is-active { z-index: 3; opacity: 1; transform: translate(-50%, -50%) scale(1); }
.intro-deck-card:not(.is-active) { z-index: 1; filter: saturate(.72) brightness(.72); }
.intro-deck-card:not(.is-active):hover { opacity: .78; }
.intro-carousel-controls { position: absolute; z-index: 5; inset: 50% -28px auto; display: flex; justify-content: space-between; pointer-events: none; transform: translateY(-50%); }
.intro-carousel-arrow { position: static; display: grid; width: 38px; height: 38px; place-items: center; padding: 0; border: 1px solid var(--drama-color-border-strong); border-radius: var(--drama-radius-full); background: var(--drama-color-surface); box-shadow: var(--drama-shadow-sm); color: var(--drama-color-text); cursor: pointer; font-size: 22px; pointer-events: auto; transform: none; }
.intro-carousel-arrow:hover { border-color: var(--drama-color-primary); color: var(--drama-color-primary); }
.intro-carousel-arrow.is-prev, .intro-carousel-arrow.is-next { position: static; }
.story-discovery-section { display: grid; align-content: start; gap: clamp(var(--drama-space-lg), 2.4vw, 34px); padding: 32px clamp(24px, 3vw, 48px) 40px; background: var(--drama-color-bg); }
.story-discovery-header { display: flex; align-items: center; justify-content: space-between; gap: var(--drama-space-xl); }
.story-discovery-header h2 { margin: 0; font-family: var(--drama-font-family-body); font-size: clamp(26px, 2.5vw, 32px); font-weight: var(--drama-font-weight-bold); letter-spacing: -.025em; }
.story-discovery-body { display: grid; align-content: start; gap: var(--drama-space-xl); }
.story-discovery-controls { position: sticky; z-index: 20; top: 56px; display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 10px 0 14px; background: color-mix(in srgb, var(--drama-color-bg) 96%, transparent); backdrop-filter: blur(14px); }
/* 搜索框放在标题行，筛选行整行留给标签，10 个常用标签加 More 在常见桌面宽度下一行放得下 */
.story-discovery-tools { display: flex; min-width: 0; align-items: center; gap: var(--drama-space-sm); }
.story-discovery-tools > label { width: min(32vw, 320px); }
.story-discovery-tools input { width: 100%; height: 36px; padding: 0 12px; border: 1px solid var(--drama-color-border); border-radius: 8px; background: var(--drama-color-bg); color: var(--drama-color-text); font-size: 12px; }
.story-discovery-tools input::placeholder { color: var(--drama-color-text-muted); }
.story-channel-rail { display: contents; }
.story-channel-rail button { min-width: max-content; height: 36px; padding: 0 14px; border: 1px solid color-mix(in srgb, var(--drama-color-border) 70%, transparent); border-radius: 8px; background: var(--drama-color-bg); color: var(--drama-color-text-muted); cursor: pointer; font-size: 12px; transition: border-color .2s ease, background .2s ease, color .2s ease; }
.story-channel-rail button:hover { border-color: var(--drama-color-border-strong); color: var(--drama-color-text); }
.story-channel-rail button.is-active { border-color: var(--drama-color-text); background: var(--drama-color-text); color: var(--drama-color-bg); }
.story-channel-rail > button.is-overflow { display: none; }
.story-channel-more { position: relative; }
.story-channel-more > button { display: inline-flex; align-items: center; gap: 8px; }
.story-channel-more > button i { width: 6px; height: 6px; margin-top: -3px; border-right: 1px solid currentColor; border-bottom: 1px solid currentColor; transform: rotate(45deg); transition: transform .2s ease; }
.story-channel-more > button[aria-expanded='true'] i { margin-top: 3px; transform: rotate(225deg); }
.story-channel-more-menu { position: absolute; z-index: 30; top: calc(100% + 6px); right: 0; display: grid; width: max-content; min-width: 200px; max-width: min(420px, 90vw); max-height: min(360px, 60vh); grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px; overflow-y: auto; padding: 6px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); box-shadow: var(--drama-shadow-lg); }
.story-channel-rail .story-channel-more-menu button { width: 100%; justify-content: flex-start; border-color: transparent; text-align: left; }
.story-discovery-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); align-items: stretch; gap: clamp(12px, 1.4vw, 22px); }
.story-discovery-grid .intro-hero { min-height: 0; aspect-ratio: auto; padding: 0; border-radius: var(--drama-radius-lg); background: var(--drama-color-text); }
.story-discovery-grid .intro-hero::before { z-index: 1; background: linear-gradient(180deg, transparent 34%, color-mix(in srgb, var(--drama-color-text) 90%, transparent)); }
.intro-feature-cover { position: absolute; inset: 0; background-position: center; background-size: cover; transition: background-image .3s ease; }
.intro-feature-video { position: absolute; z-index: 0; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0; pointer-events: none; transition: opacity .35s ease; }
.intro-feature-video.is-ready { opacity: 1; }
.story-discovery-grid .intro-carousel { z-index: 2; display: block; width: 100%; min-height: 100%; }
.story-discovery-grid .intro-content { position: absolute; right: clamp(20px, 3vw, 38px); bottom: clamp(20px, 3vw, 36px); left: clamp(20px, 3vw, 38px); max-width: none; gap: 10px; }
.story-discovery-grid .intro-eyebrow { color: var(--drama-color-primary); }
.story-discovery-grid .intro-hero h1 { color: var(--drama-color-on-primary); font-size: clamp(30px, 3.4vw, 48px); }
.story-discovery-grid .intro-summary { max-width: 620px; color: color-mix(in srgb, var(--drama-color-on-primary) 76%, transparent); }
.story-discovery-grid .intro-content .intro-actions { gap: 8px; margin-top: 6px; }
.story-discovery-grid .intro-content .primary-cta { border-color: var(--drama-color-on-primary); background: var(--drama-color-on-primary); color: var(--drama-color-text); }
.story-discovery-grid .intro-content .secondary-cta { border-color: color-mix(in srgb, var(--drama-color-on-primary) 44%, transparent); background: color-mix(in srgb, var(--drama-color-text) 48%, transparent); color: var(--drama-color-on-primary); backdrop-filter: blur(10px); }
.story-discovery-grid .intro-deck { position: absolute; inset: 0; min-height: 0; pointer-events: none; }
.story-discovery-grid .intro-deck-card { display: none; }
.story-discovery-grid .intro-carousel-controls { right: 20px; bottom: 20px; left: auto; top: auto; display: flex; gap: 8px; transform: none; }
.story-discovery-grid .intro-carousel-arrow { border-color: color-mix(in srgb, var(--drama-color-on-primary) 30%, transparent); background: color-mix(in srgb, var(--drama-color-text) 64%, transparent); color: var(--drama-color-on-primary); backdrop-filter: blur(10px); }
.story-discovery-grid .landing-stories-section { grid-column: 1 / -1; padding: 0; background: transparent; }
.story-discovery-grid .landing-stories-section .story-poster-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); gap: clamp(12px, 1.4vw, 22px); }
.story-list-stack { display: grid; gap: clamp(34px, 4vw, 56px); }
.story-list-section { display: grid; gap: var(--drama-space-lg); min-width: 0; }
.story-list-section.is-mobile-only { display: none; }
.story-list-header { display: none; align-items: center; justify-content: space-between; gap: var(--drama-space-lg); }
.story-list-header h3 { margin: 0; font-family: var(--drama-font-family-body); font-size: clamp(18px, 1.8vw, 23px); font-weight: var(--drama-font-weight-bold); letter-spacing: -.02em; }
.story-list-header a { display: grid; width: 32px; height: 32px; flex: none; place-items: center; border-radius: var(--drama-radius-full); color: var(--drama-color-text-muted); text-decoration: none; transition: background 160ms ease, color 160ms ease, transform 160ms ease; }
.story-list-header a :deep(.vg-icon) { transform: rotate(180deg); }
.story-list-header a:hover, .story-list-header a:focus-visible { background: var(--drama-color-surface-alt); color: var(--drama-color-text); transform: translateX(2px); }
.story-discovery-grid .story-poster { aspect-ratio: auto; min-height: 0; align-content: start; padding: 0; overflow: visible; border: 0; background: transparent; color: var(--drama-color-text); }
.story-discovery-grid .story-poster::before, .story-discovery-grid .story-poster::after { display: none; }
.story-discovery-grid .story-poster > img, .story-discovery-grid .story-poster > video, .story-discovery-grid .story-poster-fallback { position: relative; width: 100%; height: auto; aspect-ratio: 9 / 16; border-radius: var(--drama-radius-md); object-fit: cover; }
.story-discovery-grid .story-poster-copy { max-width: 100%; gap: 2px; padding-top: 8px; transform: none; }
.story-discovery-grid .story-poster-copy strong { display: -webkit-box; overflow: hidden; color: var(--drama-color-text); font-family: var(--drama-font-family-body); font-size: 14px; font-weight: var(--drama-font-weight-semibold); letter-spacing: 0; line-height: 1.35; text-shadow: none; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.story-poster-meta { display: flex; min-width: 0; align-items: center; gap: 8px; color: var(--drama-color-text-muted); font-size: 11px; line-height: 18px; }
.story-poster-meta > span { display: inline-flex; min-width: 0; align-items: center; gap: 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.story-poster-meta > span + span::before { margin-right: 5px; content: '·'; }
.story-discovery-grid .story-poster-copy small { display: none; }
.story-discovery-grid .story-poster:hover .story-poster-copy, .story-discovery-grid .story-poster:focus-visible .story-poster-copy { transform: none; }
.story-discovery-grid .story-poster-coming-soon { inset: 0 0 auto; aspect-ratio: 9 / 16; border-radius: var(--drama-radius-md); font-size: 16px; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
a:focus-visible, button:focus-visible, input:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 4px; }
@media (max-width: 980px) {
  .story-discovery-grid { grid-template-columns: 1fr; }
  .story-discovery-grid .landing-stories-section { grid-column: 1; }
  .story-discovery-grid .landing-stories-section .story-poster-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .story-discovery-grid .intro-hero { aspect-ratio: 16 / 10; }
  .intro-slide { grid-template-columns: 1fr; align-content: center; }
  .intro-slide.is-story { min-height: min(560px, calc(100svh - 180px)); }
  .story-catalog-grid { grid-template-columns: 1fr; }
  .game-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .landing-skeleton-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 981px) and (max-width: 1280px) {
  .game-grid, .landing-skeleton-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 680px) {
  .top-showcase { height: 390px; min-height: 0; background: radial-gradient(circle at 50% 24%, color-mix(in srgb, var(--drama-color-primary) 10%, var(--drama-color-surface-alt)), color-mix(in srgb, var(--drama-color-text) 78%, var(--drama-color-surface)) 72%); touch-action: pan-y; }
  .top-showcase-desktop-layer { display: none; }
  .top-showcase.is-video-open .top-showcase-desktop-layer { z-index: 7; display: block; }
  .top-showcase.is-video-open .top-showcase-mobile-viewport { display: none; }
  .top-showcase-mobile-viewport { position: absolute; z-index: 2; inset: 0; display: block; overflow: hidden; }
  .top-showcase-mobile-track { display: flex; width: 100%; height: 100%; will-change: transform; transition: transform 460ms cubic-bezier(.22, .74, .18, 1); }
  .top-showcase-mobile-viewport.is-dragging .top-showcase-mobile-track,
  .top-showcase-mobile-viewport.is-transition-disabled .top-showcase-mobile-track { transition: none; }
  .top-showcase-mobile-slide { position: relative; width: 100%; height: 100%; flex: 0 0 100%; overflow: hidden; }
  .top-showcase-mobile-slide::after { position: absolute; z-index: 1; inset: 0; background: linear-gradient(180deg, transparent 34%, color-mix(in srgb, var(--drama-color-text) 70%, transparent) 66%, color-mix(in srgb, var(--drama-color-text) 94%, var(--drama-color-surface)) 100%); content: ''; pointer-events: none; }
  .top-showcase::after { z-index: 1; background: linear-gradient(180deg, transparent 34%, color-mix(in srgb, var(--drama-color-text) 70%, transparent) 66%, color-mix(in srgb, var(--drama-color-text) 94%, var(--drama-color-surface)) 100%); }
  .top-showcase-cover, .top-showcase-preview { top: 12px; right: auto; bottom: auto; left: 50%; width: min(192px, calc(100% - 96px)); height: auto; aspect-ratio: 2 / 3; border-radius: var(--drama-radius-lg); box-shadow: 0 22px 60px color-mix(in srgb, var(--drama-color-text) 76%, transparent); transform: translateX(-50%); }
  .top-showcase-preview { object-fit: cover; }
  .top-showcase-mobile-link { position: absolute; z-index: 3; top: 12px; left: 50%; display: block; aspect-ratio: 2 / 3; width: min(192px, calc(100% - 96px)); border-radius: var(--drama-radius-lg); transform: translateX(-50%); }
  .top-showcase-mobile-video-trigger { padding: 0; border: 0; background: transparent; cursor: pointer; }
  .top-showcase-content { z-index: 4; top: auto; right: 32px; bottom: 16px; left: 32px; width: auto; justify-items: center; gap: 8px; text-align: center; transform: none; pointer-events: none; }
  .top-showcase-content.is-story { top: 286px; bottom: auto; align-content: start; }
  .top-showcase-content > p { display: none; }
  .top-showcase-content h1 { overflow: visible; max-width: 100%; color: var(--drama-color-on-primary); font-size: 24px; line-height: 1.2; text-overflow: clip; text-shadow: 0 2px 16px var(--drama-color-text), 0 1px 3px var(--drama-color-text); white-space: normal; }
  .top-showcase-content > span { display: -webkit-box; overflow: hidden; max-width: 100%; color: color-mix(in srgb, var(--drama-color-on-primary) 88%, transparent); font-size: 14px; font-weight: var(--drama-font-weight-normal); line-height: 1.5; text-shadow: 0 1px 8px var(--drama-color-text); -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
  .top-showcase-content.is-story .intro-actions { display: none; }
  .top-showcase-content:not(.is-story) .intro-actions { width: auto; margin-top: 4px; pointer-events: auto; }
  .top-showcase-counter { position: absolute; z-index: 6; top: 18px; right: var(--drama-space-lg); display: block; padding: 5px 9px; border: 1px solid color-mix(in srgb, var(--drama-color-on-primary) 22%, transparent); border-radius: var(--drama-radius-sm); background: color-mix(in srgb, var(--drama-color-text) 64%, transparent); color: color-mix(in srgb, var(--drama-color-on-primary) 76%, transparent); font-size: 12px; line-height: 1; backdrop-filter: blur(10px); }
  .top-showcase-dots { display: none; }
  .landing-stories-section,
  .landing-drama-section { padding: 24px var(--drama-space-lg) 32px; gap: var(--drama-space-xl); }
  .story-discovery-section,
  .landing-drama-section { padding-inline: var(--drama-space-lg); }
  .story-discovery-section { position: relative; z-index: 8; gap: var(--drama-space-lg); margin-top: -12px; padding-top: calc(var(--drama-space-md) + 12px); }
  .story-discovery-header { display: none; }
  .landing-stories-section .landing-section-header h2,
  .landing-drama-section .landing-section-header h2 { font-size: 24px; }
  .landing-drama-section :deep(.showcase-video-list) { grid-template-columns: 1fr; gap: 8px; }
  .landing-drama-section :deep(.showcase-controls) { display: none; grid-template-columns: repeat(2, minmax(0, 1fr)); padding: 8px 0 12px; background: var(--drama-color-surface); }
  .landing-drama-list.are-filters-open :deep(.showcase-controls) { display: grid; }
  .landing-drama-section :deep(.showcase-controls label:first-child) { position: static; width: 100%; min-width: 0; grid-column: 1 / -1; padding: 0; }
  .landing-drama-section :deep(.showcase-controls .script-filter),
  .landing-drama-section :deep(.showcase-controls .publish-filter) { display: grid; min-width: 0; grid-template-columns: 1fr; align-items: stretch; }
  .landing-drama-section :deep(.script-filter-select),
  .landing-drama-section :deep(.publish-filter-select) { width: 100%; min-width: 0; }
  .landing-drama-section :deep(.sort-tabs) { width: max-content; grid-column: 1 / -1; justify-self: start; margin-left: 0; }
  .landing-drama-section :deep(.showcase-results) { margin-top: 10px; }
  .landing-drama-section :deep(.game-grid) { grid-template-columns: minmax(0, 1fr); gap: 18px; }
  .landing-drama-section :deep(.game-card-copy h3) { margin-bottom: 4px; font-size: 13px; }
  .landing-drama-section :deep(.game-card-compact-meta) { gap: 5px; font-size: 11px; line-height: 20px; }
  .landing-drama-section :deep(.game-card-player-avatar) { width: 20px; height: 20px; flex-basis: 20px; font-size: 9px; }
  .landing { overflow: clip; }
  .intro-hero { height: 460px; min-height: 0; padding: 36px var(--drama-space-lg) 78px; }
  .intro-carousel { height: 100%; }
  .intro-slide { align-content: center; gap: var(--drama-space-xl); }
  .intro-slide.is-intro h1 { white-space: normal; }
  .intro-hero h1 { font-size: clamp(27px, 7.5vw, 32px); line-height: 1.04; }
  .intro-eyebrow { font-size: 10px; letter-spacing: .2em; }
  .intro-summary { font-size: 12px; line-height: 1.55; }
  .intro-actions { display: grid; width: 100%; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: stretch; gap: 8px; margin-top: 8px; }
  .intro-actions > .primary-cta { width: 100%; grid-column: 1 / -1; }
  .intro-actions > .secondary-cta { width: 100%; min-width: 0; }
  .intro-actions > .story-line-cta { grid-column: auto; grid-row: auto; }
  .intro-actions > .secondary-cta:last-child:nth-child(2) { grid-column: 1 / -1; }
  .continue-cta-group { width: 100%; grid-column: 1 / -1; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .continue-cta-group:not(:has(.story-line-cta)) { grid-template-columns: 1fr; }
  .continue-cta-group > a { width: 100%; min-width: 0; }
  .primary-cta, .secondary-cta { min-height: 42px; font-size: 11px; }
  .top-showcase-content:not(.is-story) .intro-actions > .primary-cta { width: auto; min-width: 136px; min-height: 34px; padding-inline: var(--drama-space-md); gap: 6px; box-shadow: 0 8px 24px color-mix(in srgb, var(--drama-color-text) 52%, transparent); font-size: 10px; }
  .intro-carousel-arrow { top: auto; bottom: 20px; width: 36px; height: 36px; font-size: 20px; transform: none; }
  .intro-carousel-arrow.is-prev { left: var(--drama-space-lg); }
  .intro-carousel-arrow.is-next { right: var(--drama-space-lg); }
  .intro-carousel-dots { display: none; }
  .story-discovery-grid .landing-stories-section .story-poster-grid { grid-auto-columns: 40%; grid-auto-flow: column; grid-template-columns: none; gap: 8px; overflow-x: auto; overscroll-behavior-inline: contain; padding-bottom: 4px; scroll-snap-type: inline proximity; scrollbar-width: none; }
  .story-discovery-grid .landing-stories-section .story-poster-grid::-webkit-scrollbar { display: none; }
  .story-discovery-grid .story-poster { scroll-snap-align: start; }
  .story-list-stack { gap: 32px; }
  .story-list-section { gap: 12px; }
  .story-list-section.is-mobile-only { display: grid; }
  .story-list-header { position: relative; display: flex; min-height: 40px; }
  .story-list-header a { position: absolute; inset: 0; display: flex; width: auto; height: auto; align-items: center; justify-content: flex-end; border-radius: 0; }
  .story-list-header a:hover, .story-list-header a:focus-visible { background: transparent; transform: none; }
  .story-list-header h3 { font-size: 18px; }
  .intro-hero { height: auto; padding: 42px var(--drama-space-lg) 48px; }
  .intro-carousel { height: auto; min-height: 0; grid-template-columns: 1fr; gap: var(--drama-space-2xl); }
  .intro-content { gap: var(--drama-space-md); }
  .intro-hero h1 { font-size: clamp(30px, 9vw, 42px); }
  .intro-summary { font-size: 13px; }
  .intro-deck { min-height: 300px; }
  .intro-deck-card { width: min(52vw, 196px); }
  .intro-carousel-controls { inset: 50% 0 auto; }
  .intro-carousel-arrow, .intro-carousel-arrow.is-prev, .intro-carousel-arrow.is-next { position: static; width: 36px; height: 36px; transform: none; }
  .story-poster-grid { grid-template-columns: 1fr; }
  .story-poster, .story-poster.is-left, .story-poster.is-right { min-height: 0; margin-left: 0; padding: 0; }
  .story-poster-copy { gap: 5px; }
  .story-poster-copy strong { font-size: 22px; }
  .story-poster-copy small { font-size: 11px; line-height: 1.4; -webkit-line-clamp: 2; }
  .landing-drama-section .landing-section-header { position: sticky; z-index: 21; top: 52px; align-items: center; flex-direction: row; gap: var(--drama-space-md); margin: -8px calc(var(--drama-space-lg) * -1); padding: 8px var(--drama-space-lg); background: color-mix(in srgb, var(--drama-color-surface) 96%, transparent); backdrop-filter: blur(14px); }
  .landing-drama-section .landing-section-arrow { position: absolute; inset: 0; display: flex; width: auto; height: auto; align-items: center; justify-content: flex-end; padding-inline: var(--drama-space-lg); border-radius: 0; }
  .landing-drama-section .landing-section-arrow:hover, .landing-drama-section .landing-section-arrow:focus-visible { background: transparent; transform: none; }
  .landing-drama-section .landing-section-header h2 { min-width: 0; flex: 1; }
  .showcase-controls { width: 100%; align-items: stretch; flex-direction: column; gap: var(--drama-space-sm); margin-left: 0; }
  .showcase-controls > label:first-child { width: 100%; }
  .showcase-controls input { height: 38px; padding-inline: var(--drama-space-md); font-size: 12px; }
  .showcase-controls .script-filter,
  .showcase-controls .publish-filter { display: grid; width: 100%; grid-template-columns: 60px minmax(0, 1fr); gap: var(--drama-space-xs); font-size: 11px; }
  .script-filter-select, .publish-filter-select { width: 100%; min-width: 0; }
  .script-filter-select :deep(.drama-select),
  .publish-filter-select :deep(.drama-select) { height: 38px; font-size: 12px; }
  .sort-tabs { display: grid; width: 100%; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .sort-tabs button { width: 100%; height: 38px; padding-inline: var(--drama-space-md); font-size: 12px; }
  .story-catalog { padding: 48px var(--drama-space-lg); }
  .story-card { grid-template-columns: 1fr; }
  .story-card-cover { min-height: 210px; }
  .showcase { padding: 48px var(--drama-space-lg) 80px; }
  .game-grid { grid-template-columns: 1fr; }
  .landing-skeleton-grid { grid-template-columns: 1fr; }
  .landing-footer { grid-template-columns: 1fr 1fr; padding-bottom: calc(96px + env(safe-area-inset-bottom)); }
  .footer-brand { grid-column: 1 / -1; }
  .story-discovery-controls { position: static; min-width: 0; align-items: stretch; flex-direction: column; padding: 4px 0; overflow: hidden; backdrop-filter: none; }
  .story-channel-rail { display: flex; width: 100%; min-width: 0; flex: 0 0 auto; flex-wrap: nowrap; justify-content: flex-start; gap: 6px; overflow-x: auto; overscroll-behavior-x: contain; scroll-snap-type: x proximity; scrollbar-width: none; touch-action: pan-x; -webkit-overflow-scrolling: touch; }
  .story-channel-rail::-webkit-scrollbar { display: none; }
  .story-channel-rail button { height: 34px; flex: 0 0 auto; padding-inline: 12px; border-radius: var(--drama-radius-md); background: var(--drama-color-bg); font-size: 11px; font-weight: var(--drama-font-weight-medium); scroll-snap-align: start; }
  .story-channel-rail button.is-active { border-color: var(--drama-color-primary); background: transparent; color: var(--drama-color-primary); }
  .story-channel-rail > button.is-overflow { display: inline-block; }
  .story-channel-more { display: none; }
}
@media (hover: none) { .story-poster-coming-soon { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { * { scroll-behavior: auto !important; transition: none !important; animation: none !important; } }
</style>
